import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaShoppingCart, FaSearch, FaArrowLeft, FaExternalLinkAlt, FaTimes } from 'react-icons/fa';
import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import {
    createRestaurantOrder,
    createRestaurantPayment,
    buildPosOrderLineItems,
    formatPosTableNo,
    normalizePosOrderType,
    POS_ORDER_TYPES,
    getPosOrderTypeLabel,
    fetchRestaurantOrderById,
    fetchCateringOrderById,
    fetchPosOrdersWithPayments,
    updateRestaurantOrder,
    updateDineInOrder,
    cancelRestaurantOrder,
    cancelCateringOrder,
} from './orderApi';
import { getApiErrorMessage } from './recipeApi';
import {
    fetchDailyMenuItemsByDate,
    getLocalDateString,
    parseDailyMenuByDateResponse,
    buildDailyMenuImageUrl,
    resolveDailyMenuCategoryName,
} from './dailyMenuApi';
import {
    extractFoodCategoryName,
    extractRestaurantId,
    extractRestaurantName,
    normalizeFoodCategories,
    resolveInitialRestaurant,
    fetchRestaurantPosUpiVpa,
    saveRestaurantPosUpiVpa,
} from './restaurantApi';
import { isOrderCreatedToday, parseStoreOrderDateTime, orderCreatedDateRaw } from './storeOrderDateUtils';
import { canAccessOrdersTab, canAccessUnpaidBills } from './rbacSession';
import Header from './Header';
import OrderConfirmation from './OrderConfirmation';
import DineInActiveOrders from './DineInActiveOrders';
import defaultFoodImage from './assets/DefaultFood.jpg';
import { formatFoodItemDisplayName, resolveFoodItemImageFields } from './onlineFoodItems';
import { formatMobileDisplayOrFallback, formatDisplayOrderNo, resolveDisplayOrderNo } from './formatDisplay';
import { businessAdminTab } from '../constants/appRoutes';
import { fetchCompanyTaxes } from './companyTaxApi';
import { confirmAction } from './ui/feedback';
import './css/PlaceOrder.css';

const POS_RESUME_AFTER_PRINT_KEY = 'pos_resume_after_print';

function isPosPaymentSettled(paymentStatus) {
    const s = String(paymentStatus || '').trim().toUpperCase();
    return ['SUCCESS', 'PAID', 'COMPLETED', 'COMPLETE'].includes(s);
}

function isPosOrderCancelled(order) {
    const status = String(
        order?.orderStatus ?? order?.OrderStatus ?? order?.order_status ?? ''
    )
        .trim()
        .toUpperCase();
    const code = String(
        order?.orderStatusCode ??
            order?.Order_Status_Code ??
            order?.order_status_code ??
            ''
    )
        .trim()
        .toUpperCase();
    return (
        code === 'CAN' ||
        status === 'CAN' ||
        status === 'CANCELLED' ||
        status === 'CANCELED'
    );
}

function removeOrderFromLocalOrders(orderId) {
    if (orderId == null || orderId === '') return;
    try {
        const localOrders = JSON.parse(localStorage.getItem('localOrders') || '[]');
        const next = localOrders.filter(
            (o) => String(o.orderId ?? o.orderNumber) !== String(orderId)
        );
        if (next.length !== localOrders.length) {
            localStorage.setItem('localOrders', JSON.stringify(next));
        }
    } catch {
        /* ignore */
    }
}

function formatUnpaidBillTime(order) {
    const d = parseStoreOrderDateTime(orderCreatedDateRaw(order));
    if (!d) return '';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatUnpaidBillTable(tableNumber) {
    if (tableNumber == null || tableNumber === '') return null;
    const raw = String(tableNumber).replace(/^table\s*/i, '').trim();
    return raw ? `T${raw}` : null;
}

function unpaidBillItemCount(order) {
    const items = order?.orderLineItems || order?.OrderLineItems || [];
    if (!Array.isArray(items) || items.length === 0) return 0;
    return items.reduce((sum, li) => {
        const q = Number(li.foodQuantity ?? li.quantity ?? 1);
        return sum + (Number.isFinite(q) ? q : 1);
    }, 0);
}

/** Prefer API display order number; ignore billNo that is just the internal order id. */
function pickApiDisplayOrderNo(...sources) {
    for (const src of sources) {
        if (src == null) continue;
        if (typeof src !== 'object') {
            const text = String(src).trim();
            if (text) return text;
            continue;
        }
        const orderId = src.orderId ?? src.order_id ?? src.Order_Id;
        const candidates = [
            src.displayOrderNo,
            src.display_order_no,
            src.Display_Order_No,
            src.billNo,
            src.bill_no,
        ];
        for (const candidate of candidates) {
            if (candidate == null) continue;
            const text = String(candidate).trim();
            if (!text) continue;
            if (orderId != null && String(orderId) === text) continue;
            return text;
        }
    }
    return null;
}

/** Stable cart key: daily menu id / food id — never order line id. */
function getCartItemMatchKey(item) {
    if (!item || typeof item !== 'object') return '';
    const key =
        item.dailyItemId ??
        item.Daily_Item_Id ??
        item.foodItemId ??
        item.id ??
        '';
    return key === null || key === undefined || key === '' ? '' : String(key);
}

function cartItemsMatch(a, bOrKey) {
    const keyA = getCartItemMatchKey(a);
    const keyB =
        typeof bOrKey === 'string' || typeof bOrKey === 'number'
            ? String(bOrKey)
            : getCartItemMatchKey(bOrKey);
    return Boolean(keyA) && Boolean(keyB) && keyA === keyB;
}

/** Collapse duplicate lines for the same dish into one row (sum qty). */
function coalesceCartItemsByDish(items) {
    if (!Array.isArray(items) || items.length === 0) return [];
    const byKey = new Map();
    items.forEach((item) => {
        const key = getCartItemMatchKey(item);
        const qty =
            typeof item.quantity === 'number'
                ? item.quantity
                : parseInt(item.quantity, 10) || 1;
        if (!key) {
            byKey.set(`row:${byKey.size}:${item.offlineLineItemId ?? Math.random()}`, {
                ...item,
                quantity: qty,
            });
            return;
        }
        const existing = byKey.get(key);
        if (!existing) {
            byKey.set(key, {
                ...item,
                id: item.id ?? item.foodItemId ?? key,
                foodItemId: item.foodItemId ?? item.dailyItemId ?? key,
                dailyItemId: item.dailyItemId ?? item.foodItemId ?? key,
                quantity: qty,
                total: parseFloat(item.price || 0) * qty,
            });
            return;
        }
        const nextQty = (existing.quantity || 0) + qty;
        byKey.set(key, {
            ...existing,
            quantity: nextQty,
            total: parseFloat(existing.price || 0) * nextQty,
            // Keep first saved line id so Save does not re-append this dish
            offlineLineItemId: existing.offlineLineItemId ?? item.offlineLineItemId ?? null,
        });
    });
    return [...byKey.values()];
}

/** UPI / wallet apps collect payment by scanning restaurant account QR. */
function isQrPaymentMethod(method) {
    const m = String(method || '').trim().toLowerCase();
    return ['upi', 'wallet', 'gpay', 'paytm', 'phonepe'].includes(m);
}

function posUpiVpaStorageKey(restaurantId) {
    return restaurantId != null && restaurantId !== ''
        ? `posUpiVpa:${restaurantId}`
        : 'posUpiVpa';
}

function readPosUpiVpa(restaurantId) {
    try {
        const stored = localStorage.getItem(posUpiVpaStorageKey(restaurantId));
        if (stored && String(stored).trim()) return String(stored).trim();
    } catch (_) {
        /* ignore */
    }
    const envVpa = process.env.REACT_APP_POS_UPI_VPA;
    return envVpa && String(envVpa).trim() ? String(envVpa).trim() : '';
}

function writePosUpiVpa(restaurantId, vpa) {
    const cleaned = String(vpa || '').trim();
    try {
        if (cleaned) localStorage.setItem(posUpiVpaStorageKey(restaurantId), cleaned);
        else localStorage.removeItem(posUpiVpaStorageKey(restaurantId));
    } catch (_) {
        /* ignore */
    }
    return cleaned;
}

/** Build UPI intent URL with amount so PhonePe/GPay/Paytm show the bill total. */
function buildUpiPayUrl({ vpa, payeeName, amount, note }) {
    const pa = String(vpa || '').trim();
    if (!pa) return '';
    const am = Number(amount);
    const params = new URLSearchParams();
    params.set('pa', pa);
    params.set('pn', String(payeeName || 'Restaurant').slice(0, 50));
    if (Number.isFinite(am) && am > 0) params.set('am', am.toFixed(2));
    params.set('cu', 'INR');
    if (note) params.set('tn', String(note).slice(0, 50));
    return `upi://pay?${params.toString()}`;
}

function buildQrImageUrl(data) {
    if (!data) return '';
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(data)}`;
}

const formatItemQty = (value) => {
    if (value === null || value === undefined) return '';
    const raw = String(value).trim();
    if (!raw) return '';
    const num = Number(raw);
    if (Number.isFinite(num) && num > 0) {
        return `${num} ${num === 1 ? 'pc' : 'pcs'}`;
    }
    // Non-numeric quantities (e.g., "serves 1") are shown as-is
    return raw;
};

function parseDailyMenuStockLimit(item) {
    if (!item || typeof item !== 'object') return null;
    const raw =
        item.noOfItemsAvailable ??
        item.No_of_items_available ??
        item.no_of_items_available;
    if (raw === null || raw === undefined || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : null;
}

function dailyMenuStockMessage(itemName, available, requested) {
    const label = itemName || 'Item';
    const avail = Number(available);
    if (avail <= 0) return `"${label}" is out of stock.`;
    if (requested != null) {
        return `Insufficient stock for "${label}": only ${avail} available (requested ${requested}).`;
    }
    return `Only ${avail} "${label}" available.`;
}

function isOfflineDailyMenuOrder(orderTypeFilter) {
    return orderTypeFilter === 'offline';
}

const PlaceOrder = ({
    embedded = false,
    restaurantId: restaurantIdProp = null,
    orderTypeFilter: orderTypeFilterProp = null,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedCategory, setSelectedCategory] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [menuItems, setMenuItems] = useState([]);
    const [menuDataByCategory, setMenuDataByCategory] = useState({});
    const [categories, setCategories] = useState([]);
    const [restaurantCategoryNames, setRestaurantCategoryNames] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [cart, setCart] = useState([]);
    const [mobileNumber, setMobileNumber] = useState('');
    const [customerNameOffline, setCustomerNameOffline] = useState(''); // Customer name for offline orders
    const [tableNumber, setTableNumber] = useState('');
    const [posOrderType, setPosOrderType] = useState('DINE-IN');
    const [paymentType, setPaymentType] = useState(''); // For offline orders (single select)
    const [paymentTypes, setPaymentTypes] = useState([]); // For catering orders (multiple select)
    const [restaurantId, setRestaurantId] = useState(null);
    const [restaurantData, setRestaurantData] = useState(null);
    const [isCartOpen, setIsCartOpen] = useState(true); // Default to open to match image layout
    const [mobilePosView, setMobilePosView] = useState('items');
    const [showContactModal, setShowContactModal] = useState(false);
    const [showMaximizeModal, setShowMaximizeModal] = useState(false);
    const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
    const [orderConfirmationData, setOrderConfirmationData] = useState(null);
    const [posReadyToPrint, setPosReadyToPrint] = useState(false);
    const [posReadyForNew, setPosReadyForNew] = useState(false);
    const [orderIdSearch, setOrderIdSearch] = useState('');
    const [currentOrderId, setCurrentOrderId] = useState(null); // Track if editing existing order
    const [currentDisplayOrderNo, setCurrentDisplayOrderNo] = useState(null);
    
    // Catering order fields (use orderTypeFilter from nav state so Edit from Offline tab shows offline menu)
    const [orderTypeFilter, setOrderTypeFilter] = useState(
        orderTypeFilterProp ?? location.state?.orderTypeFilter ?? location.state?.orderType ?? 'offline'
    );
    const [customerName, setCustomerName] = useState('');
    const [contactNumber, setContactNumber] = useState('');
    const [altNumber, setAltNumber] = useState('');
    const [eventDate, setEventDate] = useState('');
    const [eventLocation, setEventLocation] = useState('');
    const [numberOfGuests, setNumberOfGuests] = useState('');
    const [advancePayment, setAdvancePayment] = useState('');
    const [advancePaymentMethod, setAdvancePaymentMethod] = useState('');
    const [quotedAmount, setQuotedAmount] = useState('');
    /** GST % from Business Profile → Taxes (company-taxes API). Default 0 until loaded. */
    const [manualGst, setManualGst] = useState('0');
    const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);
    const [showPaymentTypeDropdown, setShowPaymentTypeDropdown] = useState(false);
    const [showOfflineCustomerDetailsInSidebar, setShowOfflineCustomerDetailsInSidebar] = useState(false); // For minimized sidebar: expand customer details
    const [paymentSectionExpanded, setPaymentSectionExpanded] = useState(false);
    const [maximizeCustomerDetailsOpen, setMaximizeCustomerDetailsOpen] = useState(false);
    const [unpaidBills, setUnpaidBills] = useState([]);
    const [unpaidBillsExpanded, setUnpaidBillsExpanded] = useState(false);
    const [unpaidBillsLoading, setUnpaidBillsLoading] = useState(false);
    const [showPosPaymentQr, setShowPosPaymentQr] = useState(false);
    const [showPosCardPayment, setShowPosCardPayment] = useState(false);
    /** Maximize modal right pane: unpaid bills | UPI QR | card details */
    const [maximizeRightPane, setMaximizeRightPane] = useState('unpaid');
    const [posCardLast4, setPosCardLast4] = useState('');
    const [posCardBankRrn, setPosCardBankRrn] = useState('');
    const [posCardTxnId, setPosCardTxnId] = useState('');
    const [posUpiVpa, setPosUpiVpa] = useState('');
    const [posUpiVpaDraft, setPosUpiVpaDraft] = useState('');
    const [posUpiSendPhone, setPosUpiSendPhone] = useState('');
    const [posUpiSendExpanded, setPosUpiSendExpanded] = useState(false);

    const paymentTypeDropdownRef = useRef(null);

    const user = JSON.parse(localStorage.getItem('user'));
    const userId = user ? user.userId : null;

    useEffect(() => {
        if (orderTypeFilterProp) {
            setOrderTypeFilter(orderTypeFilterProp);
        }
    }, [orderTypeFilterProp]);

    useEffect(() => {
        let cancelled = false;
        const loadPosUpiVpa = async () => {
            if (!restaurantId) {
                setPosUpiVpa('');
                setPosUpiVpaDraft('');
                return;
            }
            // Show cached value immediately, then refresh from restaurant properties.
            const cached = readPosUpiVpa(restaurantId);
            if (!cancelled && cached) {
                setPosUpiVpa(cached);
                setPosUpiVpaDraft(cached);
            }
            try {
                const vpa = await fetchRestaurantPosUpiVpa(restaurantId);
                if (cancelled) return;
                const next = vpa || cached || '';
                setPosUpiVpa(next);
                setPosUpiVpaDraft(next);
                if (vpa) writePosUpiVpa(restaurantId, vpa);
            } catch (err) {
                console.error('Failed to load restaurant UPI property:', err);
                if (!cancelled && !cached) {
                    const envFallback = process.env.REACT_APP_POS_UPI_VPA;
                    const fallback =
                        envFallback && String(envFallback).trim()
                            ? String(envFallback).trim()
                            : '';
                    setPosUpiVpa(fallback);
                    setPosUpiVpaDraft(fallback);
                }
            }
        };
        loadPosUpiVpa();
        return () => {
            cancelled = true;
        };
    }, [restaurantId]);

    useEffect(() => {
        if (restaurantIdProp != null && restaurantIdProp !== '') {
            setRestaurantId(restaurantIdProp);
            return;
        }

        const initRestaurant = async () => {
            const storedRestaurantId = localStorage.getItem('restaurantId');
            if (storedRestaurantId) {
                setRestaurantId(storedRestaurantId);
                return;
            }

            const sessionUser = JSON.parse(localStorage.getItem('user') || 'null');
            const uid = sessionUser?.userId ?? sessionUser?.user_id;
            try {
                const restaurant = await resolveInitialRestaurant(uid);
                if (!restaurant) return;
                const id = extractRestaurantId(restaurant);
                if (!id) return;
                setRestaurantId(id);
                setRestaurantData(restaurant);
                localStorage.setItem('restaurantId', String(id));
                const name = extractRestaurantName(restaurant);
                if (name) localStorage.setItem('restaurantName', name);
            } catch (error) {
                console.error('Error fetching default restaurant:', error);
            }
        };

        initRestaurant();
    }, [restaurantIdProp]);

    // Load GST % from restaurant tax config (Business Profile → Taxes)
    useEffect(() => {
        if (!restaurantId) {
            setManualGst('0');
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const rows = await fetchCompanyTaxes({ restaurantId });
                if (cancelled) return;
                const primary = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
                const raw = primary?.gstPercent ?? primary?.GST_Percent ?? primary?.gst_percent;
                const parsed = raw != null && raw !== '' ? Number(raw) : 0;
                setManualGst(Number.isFinite(parsed) && parsed >= 0 ? String(parsed) : '0');
            } catch (err) {
                console.error('Failed to load restaurant GST for POS:', err);
                if (!cancelled) setManualGst('0');
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [restaurantId]);

    useEffect(() => {
        // Generate order ID only for types that use frontend ID; offline/catering get orderId from backend API
        setTimeout(() => {
            if (!location.state?.editOrderId && orderTypeFilter !== 'offline' && orderTypeFilter !== 'catering') {
                setCurrentOrderId(prev => {
                    if (!prev) return Math.floor(Math.random() * 10000);
                    return prev;
                });
            }
        }, 100);
    }, []);

    useEffect(() => {
        const navSnapshot = location.state?.posBillSnapshot || null;
        const shouldResume =
            Boolean(location.state?.posResumeAfterPrint) ||
            Boolean(navSnapshot) ||
            Boolean(sessionStorage.getItem(POS_RESUME_AFTER_PRINT_KEY));
        if (!shouldResume) return;

        let snapshot = navSnapshot;
        if (!snapshot) {
            try {
                const raw = sessionStorage.getItem(POS_RESUME_AFTER_PRINT_KEY);
                if (raw) snapshot = JSON.parse(raw);
            } catch {
                snapshot = null;
            }
        }
        // Keep sessionStorage until New so remounts (Strict Mode) can re-apply

        if (!snapshot || typeof snapshot !== 'object') return;
        if (snapshot.savedAt && Date.now() - Number(snapshot.savedAt) > 10 * 60 * 1000) {
            try {
                sessionStorage.removeItem(POS_RESUME_AFTER_PRINT_KEY);
            } catch {
                /* ignore */
            }
            return;
        }

        setCart(Array.isArray(snapshot.cart) ? snapshot.cart : []);
        setMobileNumber(snapshot.mobileNumber || '');
        setCustomerNameOffline(snapshot.customerNameOffline || '');
        setTableNumber(snapshot.tableNumber || '');
        setPosOrderType(snapshot.posOrderType || 'DINE-IN');
        setPaymentType(snapshot.paymentType || '');
        setCurrentOrderId(snapshot.currentOrderId ?? null);
        setCurrentDisplayOrderNo(snapshot.currentDisplayOrderNo ?? null);
        setOrderConfirmationData(snapshot.orderConfirmationData || null);
        setShowMaximizeModal(snapshot.showMaximizeModal !== false);
        setMaximizeRightPane('unpaid');
        setShowPosPaymentQr(false);
        setShowPosCardPayment(false);
        setPosReadyToPrint(false);
        setPosReadyForNew(true);

        if (location.state?.posResumeAfterPrint || location.state?.posBillSnapshot) {
            navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
        }
        // Intentionally run once when returning from receipt print
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (restaurantId) {
            fetchMenuItems();
            fetchRestaurantData();
        }
    }, [restaurantId, orderTypeFilter]);

    const refreshUnpaidBills = async () => {
        if (!canAccessUnpaidBills() || orderTypeFilter !== 'offline' || !restaurantId) {
            setUnpaidBills([]);
            return;
        }
        setUnpaidBillsLoading(true);
        try {
            const apiOrders = await fetchPosOrdersWithPayments(restaurantId).catch(() => []);
            const localOrders = JSON.parse(localStorage.getItem('localOrders') || '[]');
            const byId = new Map();

            (apiOrders || []).forEach((o) => {
                const id = o.orderId ?? o.orderNumber;
                if (id == null) return;
                byId.set(String(id), o);
            });

            localOrders.forEach((o) => {
                const isCatering =
                    o.orderType === 'catering' ||
                    o.eventDate ||
                    o.eventLocation ||
                    o.numberOfGuests;
                if (isCatering) return;
                if (isPosOrderCancelled(o)) return;
                const status = String(o.orderStatus || o.OrderStatus || 'NEW').toUpperCase();
                if (status !== 'NEW') return;
                const rid = o.restaurantId || o.RestaurantId;
                if (
                    rid != null &&
                    String(rid) !== String(restaurantId)
                ) {
                    return;
                }
                const id = o.orderId ?? o.orderNumber;
                if (id == null) return;
                const key = String(id);
                const existing = byId.get(key);
                // Never revive an order the API already marked cancelled
                if (existing && isPosOrderCancelled(existing)) return;
                if (existing) {
                    const localItems = o.orderLineItems ?? o.OrderLineItems;
                    byId.set(key, {
                        ...existing,
                        ...o,
                        orderStatus: existing.orderStatus || o.orderStatus || 'NEW',
                        orderStatusCode:
                            existing.orderStatusCode || o.orderStatusCode || null,
                        paymentStatus: existing.paymentStatus ?? o.paymentStatus ?? null,
                        orderLineItems:
                            Array.isArray(localItems) && localItems.length > 0
                                ? localItems
                                : existing.orderLineItems,
                        totalPrice:
                            o.totalPrice != null && o.totalPrice !== ''
                                ? parseFloat(o.totalPrice)
                                : existing.totalPrice,
                        tableNumber: o.tableNumber ?? existing.tableNumber,
                        createdDate: o.createdDate || existing.createdDate,
                    });
                } else {
                    byId.set(key, o);
                }
            });

            const unpaid = Array.from(byId.values())
                .filter((o) => {
                    if (isPosOrderCancelled(o)) return false;
                    const status = String(o.orderStatus || 'NEW').toUpperCase();
                    if (status !== 'NEW') return false;
                    if (isPosPaymentSettled(o.paymentStatus)) return false;
                    return isOrderCreatedToday(o);
                })
                .sort((a, b) => {
                    const da = parseStoreOrderDateTime(orderCreatedDateRaw(a))?.getTime() || 0;
                    const db = parseStoreOrderDateTime(orderCreatedDateRaw(b))?.getTime() || 0;
                    return db - da;
                });

            setUnpaidBills(unpaid);
        } catch (err) {
            console.error('Failed to load unpaid bills:', err);
            setUnpaidBills([]);
        } finally {
            setUnpaidBillsLoading(false);
        }
    };

    useEffect(() => {
        refreshUnpaidBills();
    }, [restaurantId, orderTypeFilter]);

    useEffect(() => {
        const onLocalOrdersUpdated = () => {
            if (orderTypeFilter === 'offline') refreshUnpaidBills();
        };
        window.addEventListener('localOrdersUpdated', onLocalOrdersUpdated);
        return () => window.removeEventListener('localOrdersUpdated', onLocalOrdersUpdated);
    }, [restaurantId, orderTypeFilter]);

    useEffect(() => {
        if (!restaurantId) {
            setRestaurantCategoryNames([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const token = localStorage.getItem('Token');
                const response = await axiosRestaurantInstance.get(
                    RESTAURANT_API_PATHS.restaurantsFoodCategories,
                    {
                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                        timeout: 30000,
                    }
                );
                if (cancelled) return;
                const names = normalizeFoodCategories(response.data)
                    .map((row) => extractFoodCategoryName(row))
                    .map((name) => (name == null ? '' : String(name).trim()))
                    .filter(Boolean);
                setRestaurantCategoryNames(
                    [...new Set(names)].sort((a, b) =>
                        a.localeCompare(b, undefined, { sensitivity: 'base' })
                    )
                );
            } catch (err) {
                if (!cancelled) setRestaurantCategoryNames([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [restaurantId]);

    useEffect(() => {
        filterItems();
    }, [searchQuery, menuDataByCategory, selectedCategory]);

    const categoryOptions = useMemo(() => {
        const fromMenu = (categories || [])
            .map((c) => (c == null ? '' : String(c).trim()))
            .filter(Boolean);
        const merged = new Set(fromMenu);
        (restaurantCategoryNames || []).forEach((name) => {
            if (name) merged.add(name);
        });
        return [...merged].sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: 'base' })
        );
    }, [categories, restaurantCategoryNames]);

    useEffect(() => {
        if (
            selectedCategory &&
            categoryOptions.length > 0 &&
            !categoryOptions.includes(selectedCategory)
        ) {
            setSelectedCategory('');
        }
    }, [categoryOptions, selectedCategory]);

    // Function to search and load order by orderId (checks localStorage, then API)
    const applyCateringOrderToForm = (apiOrder) => {
        const rid = apiOrder.restaurantId ?? apiOrder.Restaurant_Id;
        if (rid) setRestaurantId(String(rid));
        const lineItems = (apiOrder.lineItems || []).map((li) => ({
            foodItemId: li.cateringItemId ?? li.Catering_Item_Id ?? li.Food_Item_Id,
            cateringItemId: li.cateringItemId ?? li.Catering_Item_Id ?? li.Food_Item_Id,
            cateringLineItemId: li.cateringLineItemId ?? li.Catering_Line_Item_Id,
            foodItemName: li.itemName ?? li.Item_Name ?? li.foodItemName,
            foodQuantity: li.quantity ?? li.Quantity ?? li.foodQuantity ?? 1,
            price: parseFloat((li.price ?? li.Price) || 0),
            name: li.itemName ?? li.Item_Name ?? li.foodItemName,
            quantity: li.quantity ?? li.Quantity ?? li.foodQuantity ?? 1,
        }));
        const cartItems = lineItems.map((item) => ({
            foodItemId: item.foodItemId,
            cateringItemId: item.cateringItemId,
            cateringLineItemId: item.cateringLineItemId,
            foodItemName: item.foodItemName,
            name: item.foodItemName,
            price: item.price,
            quantity: item.foodQuantity,
        }));
        setCart(cartItems);
        setMobileNumber(apiOrder.customerContact ?? apiOrder.customerMobile ?? '');
        setCustomerNameOffline(apiOrder.customerName ?? apiOrder.Customer_Name ?? '');
        setCustomerName(apiOrder.customerName ?? apiOrder.Customer_Name ?? '');
        setContactNumber(apiOrder.customerContact ?? apiOrder.customerMobile ?? '');
        setAltNumber(apiOrder.altNumber ?? apiOrder.Alt_Number ?? '');
        setEventDate(apiOrder.eventDate ?? apiOrder.Event_Date ?? '');
        setEventLocation(
            apiOrder.deliveryAddress ?? apiOrder.eventLocation ?? apiOrder.Event_Location ?? ''
        );
        setNumberOfGuests(apiOrder.numberOfGuests ?? apiOrder.Number_Of_Guests ?? '');
        setAdvancePayment(apiOrder.advancePayment ?? apiOrder.Advance_Payment ?? '');
        setTableNumber('');
        setPaymentType('');
        setPaymentTypes([]);
        const quoted =
            apiOrder.totalPrice ??
            apiOrder.Total_Price ??
            apiOrder.quotedTotal ??
            apiOrder.Quoted_Total;
        setQuotedAmount(
            quoted != null && quoted !== ''
                ? String(Math.round(parseFloat(quoted) * 100) / 100)
                : ''
        );
        setCurrentOrderId(apiOrder.cateringOrderId ?? apiOrder.orderId);
        setCurrentDisplayOrderNo(resolveDisplayOrderNo(apiOrder));
    };

    const applyPosOrderToForm = (apiOrder) => {
        if (!apiOrder) return;
        setPosReadyToPrint(false);
        setPosReadyForNew(false);
        setOrderConfirmationData(null);
        const orderId = apiOrder.orderId ?? apiOrder.orderNumber;
        setCurrentDisplayOrderNo(resolveDisplayOrderNo(apiOrder));
        const lineItems = Array.isArray(apiOrder.lineItems)
            ? apiOrder.lineItems
            : apiOrder.orderLineItems || apiOrder.OrderLineItems || [];
        const cartItems = coalesceCartItemsByDish(
            lineItems.map((li) => {
                const dailyItemId =
                    li.dailyItemId ??
                    li.Daily_Item_Id ??
                    li.daily_item_id ??
                    null;
                // Never key cart rows by orderLineId — that makes every Add create a duplicate.
                const dishKey = dailyItemId ?? li.foodItemId ?? null;
                return {
                    foodItemId: dishKey,
                    id: dishKey,
                    dailyItemId: dailyItemId ?? dishKey,
                    foodItemName: li.foodItemName ?? li.Food_Item_Name ?? li.name ?? 'Item',
                    name: li.foodItemName ?? li.Food_Item_Name ?? li.name ?? 'Item',
                    quantity: li.foodQuantity ?? li.Food_Quantity ?? li.quantity ?? 1,
                    price: parseFloat(li.price ?? li.Price ?? 0),
                    offlineLineItemId: li.orderLineId ?? li.offlineLineItemId ?? null,
                };
            })
        );
        setCart(cartItems.length > 0 ? cartItems : []);
        setMobileNumber(apiOrder.customerPhone ?? apiOrder.customerMobile ?? apiOrder.mobile ?? '');
        setCustomerNameOffline(apiOrder.customerName ?? apiOrder.CustomerName ?? '');
        const apiTableNumber = apiOrder.tableNumber ?? apiOrder.tableNo ?? null;
        setTableNumber(
            apiTableNumber != null && apiTableNumber !== ''
                ? String(apiTableNumber).replace(/^table\s*/i, '').trim()
                : ''
        );
        if (apiOrder.orderType) {
            setPosOrderType(normalizePosOrderType(apiOrder.orderType));
        }
        setPaymentType('');
        setPaymentTypes([]);
        setAdvancePayment('');
        setPaymentSectionExpanded(false);
        setShowOfflineCustomerDetailsInSidebar(false);
        setOrderIdSearch(orderId != null ? String(orderId) : '');
        setCurrentOrderId(orderId);
        setCurrentDisplayOrderNo(resolveDisplayOrderNo(apiOrder));
        setIsCartOpen(true);
        setMobilePosView('cart');
    };

    const loadUnpaidBill = async (bill) => {
        if (!canAccessUnpaidBills() || !bill) return;
        const orderId = bill.orderId ?? bill.orderNumber;
        if (orderId == null) return;

        const rid =
            restaurantId || localStorage.getItem('restaurantId') || null;
        try {
            if (rid) {
                const apiOrder = await fetchRestaurantOrderById(rid, orderId);
                if (apiOrder && (apiOrder.orderId != null || apiOrder.orderNumber != null)) {
                    applyPosOrderToForm({
                        ...apiOrder,
                        tableNumber: apiOrder.tableNumber ?? bill.tableNumber,
                        customerName: apiOrder.customerName ?? bill.customerName,
                        customerPhone:
                            apiOrder.customerPhone ??
                            bill.customerMobile ??
                            bill.mobile,
                    });
                    setMaximizeRightPane('unpaid');
                    setShowMaximizeModal(true);
                    return;
                }
            }
            applyPosOrderToForm(bill);
            setMaximizeRightPane('unpaid');
            setShowMaximizeModal(true);
        } catch (err) {
            console.error('Failed to load unpaid bill:', err);
            applyPosOrderToForm(bill);
            setMaximizeRightPane('unpaid');
            setShowMaximizeModal(true);
        }
    };

    const handleSearchOrder = async (searchValue = null) => {
        const searchId = (searchValue !== null ? searchValue : orderIdSearch)?.trim();
        
        if (!searchId || searchId === '') {
            return;
        }

        try {
            // Catering orders are stored on the server — load from API first.
            if (orderTypeFilter === 'catering') {
                const apiOrder = await fetchCateringOrderById(searchId);
                if (apiOrder && (apiOrder.cateringOrderId != null || apiOrder.orderId != null)) {
                    applyCateringOrderToForm(apiOrder);
                    return;
                }
                if (searchValue === null) {
                    alert(`Catering order #${searchId} not found`);
                }
                return;
            }

            const localOrders = JSON.parse(localStorage.getItem('localOrders') || '[]');
            
            // Search for order by orderId or orderNumber - handle different formats
            const foundOrder = localOrders.find(order => 
                String(order.orderId) === searchId || 
                String(order.orderNumber) === searchId ||
                String(order.displayOrderNo || '') === searchId ||
                String(order.display_order_no || '') === searchId ||
                order.orderId === Number(searchId) ||
                order.orderNumber === Number(searchId) ||
                String(order.orderId || '') === String(searchId) ||
                String(order.orderNumber || '') === String(searchId)
            );
            
            console.log('Searching for order ID:', searchId);
            console.log('Total orders in localStorage:', localOrders.length);
            console.log('Found order:', foundOrder);

            if (!foundOrder) {
                // Dine-in: load from GET /restaurants/:id/orders (orderSource MB)
                const ridForPos =
                    restaurantId || localStorage.getItem('restaurantId') || null;
                if (orderTypeFilter === 'offline' && ridForPos) {
                    try {
                        const apiOrder = await fetchRestaurantOrderById(ridForPos, searchId);
                        const orderId = apiOrder?.orderId;
                        if (apiOrder && orderId != null) {
                            const lineItems = Array.isArray(apiOrder.lineItems)
                                ? apiOrder.lineItems
                                : apiOrder.orderLineItems || [];
                            const cartItems = lineItems.map((li) => ({
                                foodItemId: li.dailyItemId ?? li.foodItemId ?? li.orderLineId,
                                foodItemName: li.foodItemName ?? li.Food_Item_Name ?? 'Item',
                                name: li.foodItemName ?? li.Food_Item_Name ?? 'Item',
                                quantity: li.foodQuantity ?? li.Food_Quantity ?? 1,
                                price: parseFloat(li.price ?? li.Price ?? 0),
                                offlineLineItemId: li.orderLineId ?? li.offlineLineItemId ?? null,
                            }));
                            setCart(cartItems.length > 0 ? cartItems : []);
                            setMobileNumber(apiOrder.customerPhone ?? '');
                            setCustomerNameOffline(apiOrder.customerName ?? '');
                            const apiTableNumber = apiOrder.tableNumber ?? null;
                            setTableNumber(
                                apiTableNumber != null && apiTableNumber !== ''
                                    ? String(apiTableNumber).replace(/^table\s*/i, '').trim()
                                    : ''
                            );
                            if (apiOrder.orderType) {
                                setPosOrderType(normalizePosOrderType(apiOrder.orderType));
                            }
                            setPaymentType('');
                            setPaymentTypes([]);
                            setAdvancePayment('');
                            setCurrentOrderId(orderId);
                            return;
                        }
                    } catch (apiErr) {
                        console.error('API fetch dine-in order by ID:', apiErr?.response?.data || apiErr);
                    }
                }
                if (searchValue === null) {
                    alert(`Order ID ${searchId} not found`);
                }
                return;
            }

            // Populate cart with order items - handle different property names
            const orderItems = foundOrder.orderLineItems || foundOrder.OrderLineItems || [];
            const cartItems = orderItems.map(item => ({
                foodItemId: item.foodItemId || item.FoodItemId,
                foodItemName: item.foodItemName || item.FoodItemName || item.name || 'N/A',
                name: item.foodItemName || item.FoodItemName || item.name || 'N/A',
                price: parseFloat(item.price || item.Price || 0),
                quantity: item.foodQuantity || item.FoodQuantity || item.quantity || item.Quantity || 1,
                offlineLineItemId: item.offlineLineItemId ?? item.orderLineId ?? null,
            }));

            setCart(cartItems);

            // Populate form fields - always set, even if empty
            setMobileNumber(foundOrder.mobile || '');
            setCustomerNameOffline(foundOrder.customerName || foundOrder.CustomerName || '');
            setTableNumber(foundOrder.tableNumber ? String(foundOrder.tableNumber) : '');
            const loadedOrderType = foundOrder.orderType ?? foundOrder.Order_Type;
            if (loadedOrderType) setPosOrderType(normalizePosOrderType(loadedOrderType));
            // Handle payment method - could be string or array
            const paymentMethod = foundOrder.paymentMethod || '';
            if (foundOrder.orderType === 'catering' || foundOrder.eventDate || foundOrder.eventLocation) {
                // For catering orders, paymentMethod might be an array or comma-separated string
                if (Array.isArray(paymentMethod)) {
                    setPaymentTypes(paymentMethod);
                } else if (typeof paymentMethod === 'string' && paymentMethod.includes(',')) {
                    setPaymentTypes(paymentMethod.split(',').map(p => p.trim()));
                } else if (paymentMethod) {
                    setPaymentTypes([paymentMethod]);
                } else {
                    setPaymentTypes([]);
                }
                setPaymentType('');
            } else {
                setPaymentType(paymentMethod);
                setPaymentTypes([]);
            }
            setAdvancePayment(foundOrder.advancePayment || foundOrder.advance_payment || '');
            if (foundOrder.orderType === 'catering' || foundOrder.eventDate || foundOrder.eventLocation || foundOrder.numberOfGuests) {
                const quoted = foundOrder.totalPrice ?? foundOrder.Total_Price;
                setQuotedAmount(
                    quoted != null && quoted !== ''
                        ? String(Math.round(parseFloat(quoted) * 100) / 100)
                        : ''
                );
            }
            
            // Populate catering fields if it's a catering order (do not override when editing from Offline tab)
            const isEditFromNavigation = location.state?.editOrderId != null;
            if (!isEditFromNavigation && (foundOrder.orderType === 'catering' || foundOrder.eventDate || foundOrder.eventLocation || foundOrder.numberOfGuests)) {
                setOrderTypeFilter('catering');
                setAdvancePaymentMethod(foundOrder.advancePaymentMethod || foundOrder.advance_payment_method || '');
                setCustomerName(foundOrder.customerName || foundOrder.CustomerName || '');
                setContactNumber(foundOrder.mobile || foundOrder.customerMobile || foundOrder.contactNumber || '');
                setAltNumber(foundOrder.altNumber || foundOrder.Alt_Number || '');
                setEventDate(foundOrder.eventDate || '');
                setEventLocation(foundOrder.eventLocation || foundOrder.EventLocation || '');
                setNumberOfGuests(foundOrder.numberOfGuests || foundOrder.NumberOfGuests || '');
            } else if (foundOrder.orderType === 'catering' || foundOrder.eventDate || foundOrder.eventLocation || foundOrder.numberOfGuests) {
                setAdvancePaymentMethod(foundOrder.advancePaymentMethod || foundOrder.advance_payment_method || '');
                setCustomerName(foundOrder.customerName || foundOrder.CustomerName || '');
                setContactNumber(foundOrder.mobile || foundOrder.customerMobile || foundOrder.contactNumber || '');
                setAltNumber(foundOrder.altNumber || foundOrder.Alt_Number || '');
                setEventDate(foundOrder.eventDate || '');
                setEventLocation(foundOrder.eventLocation || foundOrder.EventLocation || '');
                setNumberOfGuests(foundOrder.numberOfGuests || foundOrder.NumberOfGuests || '');
            }

            // Set current order ID to track that we're editing an existing order
            setCurrentOrderId(foundOrder.orderId);
            
            console.log('Order loaded successfully:', {
                orderId: foundOrder.orderId,
                cartItems: cartItems.length,
                mobile: foundOrder.mobile,
                tableNumber: foundOrder.tableNumber,
                paymentMethod: foundOrder.paymentMethod
            });

            // Order loaded successfully - no alert needed
        } catch (error) {
            console.error('Error searching order:', error);
            if (searchValue === null) {
                alert('Error loading order. Please try again.');
            }
        }
    };

    // Check if editing an order from navigation
    useEffect(() => {
        const editOrderId = location.state?.editOrderId;
        if (!editOrderId) return;

        const orderIdString = String(editOrderId);
        console.log('Loading order from navigation:', orderIdString);
        console.log('Location state:', location.state);
        
        // Set search input first
        setOrderIdSearch(orderIdString);
        
        // Load the order immediately - use a small delay to ensure state is set
        const loadOrder = async () => {
            try {
                if (orderTypeFilter === 'catering') {
                    await handleSearchOrder(orderIdString);
                    return;
                }

                const localOrders = JSON.parse(localStorage.getItem('localOrders') || '[]');
                console.log('All orders in localStorage:', localOrders);
                
                const foundOrder = localOrders.find(order => 
                    String(order.orderId) === orderIdString || 
                    String(order.orderNumber) === orderIdString ||
                    order.orderId === Number(orderIdString) ||
                    order.orderNumber === Number(orderIdString)
                );
                
                console.log('Found order:', foundOrder);
                
                if (foundOrder) {
                    handleSearchOrder(orderIdString);
                } else {
                    await handleSearchOrder(orderIdString);
                }
            } catch (error) {
                console.error('Error loading order:', error);
                alert('Error loading order. Please try again.');
            }
        };
        
        // Small delay to ensure state updates
        setTimeout(loadOrder, 50);
    }, [location.state?.editOrderId, orderTypeFilter]);

    // Close payment dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showPaymentDropdown && !event.target.closest('.payment-type-group')) {
                setShowPaymentDropdown(false);
            }
        };
        
        if (showPaymentDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showPaymentDropdown]);

    // Close payment type dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (paymentTypeDropdownRef.current && !paymentTypeDropdownRef.current.contains(event.target)) {
                setShowPaymentTypeDropdown(false);
            }
        };
        
        if (showPaymentTypeDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showPaymentTypeDropdown]);

    useEffect(() => {
        if (cart.length === 0) {
            setPaymentSectionExpanded(false);
            setShowPaymentTypeDropdown(false);
            setShowOfflineCustomerDetailsInSidebar(false);
        }
    }, [cart.length]);

    // Auto-search when orderIdSearch changes (with debounce)
    useEffect(() => {
        if (!orderIdSearch || orderIdSearch.trim() === '') {
            // Clear order details when search is empty
            setCart([]);
            setMobileNumber('');
            setCustomerNameOffline('');
            setTableNumber('');
            setPaymentType('');
            setAdvancePayment('');
            setQuotedAmount('');
            // For offline and catering, orderId comes from backend on save; don't set random ID so API is called
            if (orderTypeFilter === 'offline' || orderTypeFilter === 'catering') {
                setCurrentOrderId(null);
            } else {
                const newOrderId = Math.floor(Math.random() * 10000);
                setCurrentOrderId(newOrderId);
            }
            return;
        }

        // Skip auto-search if this is from navigation (already handled above)
        if (location.state?.editOrderId && String(location.state.editOrderId) === orderIdSearch.trim()) {
            return;
        }

        // Debounce the search to avoid searching on every keystroke
        const timeoutId = setTimeout(() => {
            handleSearchOrder(orderIdSearch);
        }, 500); // Wait 500ms after user stops typing

        return () => clearTimeout(timeoutId);
    }, [orderIdSearch]);

    // Keep cart open by default - no need to auto-open since it's already open

    const fetchMenuItems = async () => {
        try {
            const token = localStorage.getItem('Token');
            let itemsArray = [];
            
            // For catering orders, fetch from GET /catering/menu/:restaurantId
            if (orderTypeFilter === 'catering') {
                try {
                    const response = await axiosRestaurantInstance.get(`/catering/menu/${restaurantId}`, {
                        headers: { 'Authorization': `Bearer ${token}` },
                    });
                    const data = response.data || {};
                    const rawItems = (data?.items && Array.isArray(data.items))
                        ? data.items
                        : (data?.cateringItemId != null ? [data] : (Array.isArray(data) ? data : (data && typeof data === 'object' ? Object.values(data).flat() : [])));
                    itemsArray = rawItems.map((item) => {
                        const imagePath = item.imagePath != null && String(item.imagePath).trim() ? item.imagePath : null;
                        const rawImageUrl = item.imageUrl != null && String(item.imageUrl).trim() ? item.imageUrl : null;
                        const imageUrl = rawImageUrl
                            ? String(rawImageUrl).trim().replace(/^%22+|%22+$/g, '').replace(/^"+|"+$/g, '').trim()
                            : null;
                        return {
                            Food_Item_Id: item.cateringItemId ?? item.Catering_Item_Id ?? item.Food_Item_Id,
                            foodCategoryId: item.foodCategoryId ?? item.food_category_id,
                            foodCategory: resolveDailyMenuCategoryName(item),
                            Food_Category: resolveDailyMenuCategoryName(item),
                            Food_Type: item.foodType ?? item.itemFoodType ?? item.Food_Type ?? '',
                            itemFoodType: item.foodType ?? item.itemFoodType ?? 'VEG',
                            Food_Item: item.itemName ?? item.Item_Name ?? item.item_name ?? item.Food_Item ?? item.foodItem ?? '',
                            foodItemName: item.itemName ?? item.Item_Name ?? item.item_name ?? item.Food_Item ?? item.foodItem ?? '',
                            Price: item.price ?? item.Price,
                            price: parseFloat(item.price ?? item.Price ?? 0),
                            Serving_Size: item.quantity ?? item.servingSize ?? item.Serving_Size ?? item.Quantity ?? 'serves 1',
                            Quantity: item.quantity ?? item.servingSize ?? item.Serving_Size ?? item.Quantity ?? 'serves 1',
                            Description: item.description ?? item.Description ?? '',
                            imagePath,
                            imageUrl: imageUrl || undefined,
                        };
                    });
                } catch (cateringErr) {
                    console.error('Error fetching catering menu:', cateringErr);
                }
            } else if (orderTypeFilter === 'offline') {
                const menuDate = getLocalDateString();
                const data = await fetchDailyMenuItemsByDate(restaurantId, menuDate, {
                    menuSource: 'DINEIN',
                });
                itemsArray = parseDailyMenuByDateResponse(data);
            } else {
                // For online orders, fetch regular food items from API
                const response = await axiosRestaurantInstance.get(
                    RESTAURANT_API_PATHS.restaurantFoodItemsByRestaurantId(restaurantId),
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                
                if (response.data) {
                    // Convert object to array if needed
                    const menuItemsObj = response.data;
                    itemsArray = Object.values(menuItemsObj).flat();
                }
            }
            
            if (itemsArray.length > 0) {
                setMenuItems(itemsArray);

                const resolveMenuItemImageUrl = (item) => {
                    const { imageUrl, imagePath } = resolveFoodItemImageFields(item);
                    if (imageUrl) {
                        return String(imageUrl)
                            .trim()
                            .replace(/^%22+|%22+$/g, '')
                            .replace(/^"+|"+$/g, '')
                            .trim();
                    }
                    return buildDailyMenuImageUrl(imagePath) || '';
                };
                // Group items by menu category (not diet type)
                const groupedByCategory = {};
                itemsArray.forEach(item => {
                    const category = resolveDailyMenuCategoryName(item);
                    const dietType =
                        item.itemFoodType ??
                        item.Item_Food_Type ??
                        item.foodType ??
                        item.Food_Type ??
                        '';
                    const dailyItemId =
                        item.dailyItemId ??
                        item.Daily_Item_Id ??
                        item.DailyItemId ??
                        item.dailyMenuItemId;
                    const foodItemId =
                        dailyItemId ??
                        item.Food_Item_Id ??
                        item.foodItemId ??
                        item.FoodItemId;
                    const foodItemName =
                        item.foodItem ??
                        item.Food_Item ??
                        item.FoodItem ??
                        item.foodItemName ??
                        item.FoodItemName ??
                        item.Item_Name ??
                        item.item_name ??
                        item.itemName ??
                        '';
                    const description = item.Description || item.description || '';
                    const itemQuantity =
                        item.quantity ??
                        item.Quantity ??
                        item.foodQuantity ??
                        item.Food_Quantity ??
                        item.servingSize ??
                        item.Serving_Size ??
                        '';
                    const price = item.Price || item.price || 0;
                    const imageUrl = resolveMenuItemImageUrl(item);
                    
                    if (!groupedByCategory[category]) {
                        groupedByCategory[category] = [];
                    }
                    groupedByCategory[category].push({
                        id: foodItemId,
                        dailyItemId: dailyItemId ?? foodItemId,
                        foodItemId: foodItemId,
                        name: foodItemName,
                        foodItemName: foodItemName,
                        description: description,
                        quantity: itemQuantity,
                        noOfItemsAvailable:
                            item.noOfItemsAvailable ??
                            item.No_of_items_available ??
                            item.no_of_items_available ??
                            null,
                        price: parseFloat(price || 0),
                        category,
                        foodType: dietType,
                        imageUrl: imageUrl || defaultFoodImage
                    });
                });
                
                setMenuDataByCategory(groupedByCategory);
                setCategories(
                    Object.keys(groupedByCategory)
                        .map((c) => String(c).trim())
                        .filter(Boolean)
                        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                );
            } else {
                setMenuItems([]);
                setMenuDataByCategory({});
                setCategories([]);
                setFilteredItems([]);
            }
        } catch (error) {
            console.error('Error fetching menu items:', error);
        }
    };

    const fetchRestaurantData = async () => {
        try {
            const token = localStorage.getItem('Token');
            const response = await axiosRestaurantInstance.get(RESTAURANT_API_PATHS.restaurantUserDefault(userId), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });
            if (response.data) {
                setRestaurantData(response.data);
            }
        } catch (error) {
            console.error('Error fetching restaurant data:', error);
        }
    };

    // Create POS order via POST /restaurants/orders
    const createOfflineOrderViaApi = async (payload) => {
        const createdById = payload.createdBy ?? (userId ? Number(userId) : 1);
        const gstPercent =
            manualGst !== '' && !isNaN(parseFloat(manualGst))
                ? Math.max(0, parseFloat(manualGst))
                : 0;
        const lineItems = buildPosOrderLineItems(payload.cart);
        if (!lineItems.length) {
            throw new Error('Cart items must have a valid daily menu item id');
        }
        const itemsSubtotal = lineItems.reduce(
            (sum, li) => sum + Number(li.price || 0) * Number(li.foodQuantity || 0),
            0
        );
        const gstAmount = Math.round(itemsSubtotal * gstPercent) / 100;
        const orderType = normalizePosOrderType(payload.posOrderType ?? posOrderType);
        const tableRaw = payload.tableNo ?? payload.tableNumber ?? tableNumber;
        return createRestaurantOrder({
            restaurantId: Number(restaurantId),
            orderType,
            tableNo: orderType === 'DINE-IN' ? formatPosTableNo(tableRaw) : '',
            orderSource: 'MB',
            deliveryContactName: payload.customerName || 'N/A',
            deliveryContactNumber: payload.customerPhone || '',
            createdById,
            lineItems,
            gst: gstAmount,
            deliveryFee: 0,
            convienceFee: 0,
            totalPrice: Math.round((itemsSubtotal + gstAmount) * 100) / 100,
            orderStatus: payload.orderStatus ?? 'NEW',
        });
    };

    const posOrderStatusToCode = (status) => {
        const norm = String(status ?? 'NEW').trim().toUpperCase();
        const map = { NEW: 'NEW', COMPLETED: 'CMP', COMPLETE: 'CMP'};
        return map[norm] || 'NEW';
    };

    /** PUT /restaurants/orders/:id — status and customer contact only (line items stay in localStorage). */
    const updatePosOrderViaApi = async (orderId, payload) => {
        const orderStatus = payload.orderStatus ?? 'NEW';
        return updateRestaurantOrder(orderId, {
            orderStatus,
            orderStatusCode: posOrderStatusToCode(orderStatus),
            customerName: payload.customerName ?? 'N/A',
            customerPhone: payload.customerPhone ?? '',
            orderType: payload.posOrderType
                ? normalizePosOrderType(payload.posOrderType)
                : undefined,
            updatedById: payload.updatedBy ?? (userId ? Number(userId) : 1),
        });
    };

    // Create catering order via POST /catering/order - returns cateringOrderId
    const createCateringOrderViaApi = async (payload) => {
        const token = localStorage.getItem('Token');
        // Normalize Event_Date to YYYY-MM-DD (API requires this format, does not accept null)
        let eventDateFormatted = payload.eventDate && String(payload.eventDate).trim() ? String(payload.eventDate).trim() : null;
        if (eventDateFormatted) {
            const parts = eventDateFormatted.split(/[/-]/);
            if (parts.length === 3 && parts[0].length <= 2) {
                eventDateFormatted = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
            }
        }
        if (!eventDateFormatted) {
            eventDateFormatted = new Date().toISOString().split('T')[0];
        }
        const createdBy = userId ? Number(userId) : 1;
        const lineItems = (payload.cart || []).map((item) => {
            const qty = typeof item.quantity === 'number' ? item.quantity : (parseInt(item.quantity, 10) || 1);
            const unitPrice = parseFloat(item.price || 0);
            const cateringItemId =
                item.cateringItemId ?? item.foodItemId ?? item.Food_Item_Id ?? item.id;
            return {
                cateringItemId: Number(cateringItemId),
                itemName: item.foodItemName || item.name || 'Item',
                quantity: qty,
                price: unitPrice,
                createdBy,
            };
        });
        const body = {
            customerName: payload.customerName || 'N/A',
            customerContact: payload.contactNumber || '',
            altNumber: payload.altNumber || undefined,
            restaurantId: Number(restaurantId),
            eventDate: eventDateFormatted,
            eventTime: payload.eventTime || '12:00:00',
            eventType: payload.eventType || 'Catering',
            deliveryAddress: payload.deliveryAddress || payload.eventLocation || '',
            deliveryInstructions: payload.deliveryInstructions || '',
            numberOfGuests: payload.numberOfGuests ? Number(payload.numberOfGuests) : undefined,
            createdBy,
            orderStatusCode: payload.orderStatus || 'NEW',
            lineItems,
        };
        const quoted = parseFloat(payload.totalPrice ?? payload.quotedAmount);
        if (Number.isFinite(quoted) && quoted >= 0) {
            body.totalPrice = quoted;
        }
        const res = await axiosRestaurantInstance.post(
            RESTAURANT_API_PATHS.cateringOrder,
            body,
            { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        return res.data;
    };

    /** POST /restaurants/payments after POS order exists on server. */
    const createPosPaymentViaApi = async (orderId, amount, customerName, paymentMethod, extras = {}) => {
        const method = paymentMethod || 'CASH';
        const isCash = String(method).trim().toUpperCase() === 'CASH';
        return createRestaurantPayment({
            orderId,
            amount: parseFloat(amount) || 0,
            bankRRN: isCash ? '' : extras.bankRRN ?? '',
            transactionId: isCash ? '' : extras.transactionId || undefined,
            paymentMethod: method,
            customerDetails: customerName || 'N/A',
            status: 'SUCCESS',
            createdById: userId ? Number(userId) : 1,
        });
    };

    // Update catering order (PUT /catering/order/{orderId})
    // Same pattern as offline: Created_By for new/added line items, refetch to merge existing line ids when needed
    const updateCateringOrderViaApi = async (orderId, payload) => {
        try {
            const token = localStorage.getItem('Token');
            const updatedBy = payload.updatedBy ?? (userId ? Number(userId) : 1);
            const createdBy = payload.createdBy ?? updatedBy;
            let cart = payload.cart || payload.lineItems || [];
            // If any cart item is missing Catering_Item_Id, refetch order to get ids and merge by index (like offline)
            const missingId = cart.some((item) => {
                const id = item.foodItemId ?? item.cateringItemId ?? item.id;
                return id == null || id === 0 || Number.isNaN(Number(id));
            });
            if (missingId && cart.length > 0) {
                try {
                    const existing = await fetchCateringOrderById(orderId);
                    const apiLines = Array.isArray(existing?.lineItems) ? existing.lineItems : [];
                    cart = cart.map((item, idx) => {
                        const apiLine = apiLines[idx];
                        const id = item.foodItemId ?? item.cateringItemId ?? item.id ?? apiLine?.cateringItemId ?? apiLine?.Catering_Item_Id;
                        const lineId = item.cateringLineItemId ?? apiLine?.cateringLineItemId ?? apiLine?.Catering_Line_Item_Id;
                        return { ...item, foodItemId: id, cateringItemId: id, cateringLineItemId: lineId ?? item.cateringLineItemId };
                    });
                } catch (e) {
                    console.warn('Could not refetch catering order for line item ids:', e?.message);
                }
            }
            const lineItems = cart.map((item) => {
                const qty = typeof item.quantity === 'number' ? item.quantity : (parseInt(item.quantity, 10) || 1);
                const price = parseFloat(item.price || 0);
                const cateringItemId =
                    item.cateringItemId ?? item.foodItemId ?? item.id;
                return {
                    cateringItemId: Number(cateringItemId),
                    itemName: item.foodItemName ?? item.itemName ?? item.name ?? 'Item',
                    quantity: qty,
                    price: Number.isFinite(price) ? price : 0,
                };
            });
            const body = {
                orderStatus: payload.orderStatus ?? 'NEW',
                customerName: payload.customerName,
                customerContact: payload.contactNumber ?? payload.deliveryContactNumber ?? '',
                altNumber: payload.altNumber ?? '',
                deliveryAddress: payload.eventLocation ?? payload.deliveryAddress,
                numberOfGuests: payload.numberOfGuests ? Number(payload.numberOfGuests) : undefined,
                deliveryContactName: payload.deliveryContactName ?? null,
                deliveryContactNumber: payload.contactNumber ?? payload.deliveryContactNumber ?? null,
                updatedBy,
                lineItems: lineItems.length > 0 ? lineItems : [],
            };
            const quoted = parseFloat(payload.totalPrice ?? payload.quotedAmount);
            if (Number.isFinite(quoted) && quoted >= 0) {
                body.totalPrice = quoted;
            }
            const res = await axiosRestaurantInstance.put(
                RESTAURANT_API_PATHS.cateringOrderById(orderId),
                body,
                { headers: token ? { Authorization: `Bearer ${token}` } : {} }
            );
            return res?.data ?? null;
        } catch (err) {
            console.error('Update catering order API error:', err?.response?.data ?? err?.message);
            throw err;
        }
    };

    const filterItems = () => {
        if (searchQuery.trim() === '') {
            // No search query: show selected category, or all items when "All categories"
            const categoryItems = selectedCategory
                ? (menuDataByCategory[selectedCategory] || [])
                : Object.values(menuDataByCategory).flat();
            setFilteredItems(categoryItems);
        } else {
            // If search query exists, search across all categories
            const allItems = Object.values(menuDataByCategory).flat();
            const filtered = allItems.filter(item =>
                item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.foodItemName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.description?.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setFilteredItems(filtered);
        }
    };

    const validateOfflineCartQuantity = (item, nextQty) => {
        if (!isOfflineDailyMenuOrder(orderTypeFilter)) return true;
        const limit = parseDailyMenuStockLimit(item);
        if (limit == null) return true;
        if (nextQty <= limit) return true;
        alert(
            dailyMenuStockMessage(
                item.foodItemName || item.name || item.Food_Item,
                limit,
                nextQty
            )
        );
        return false;
    };

    const isAtDailyMenuStockLimit = (item, cartQty) => {
        if (!isOfflineDailyMenuOrder(orderTypeFilter)) return false;
        const limit = parseDailyMenuStockLimit(item);
        if (limit == null) return false;
        return cartQty >= limit;
    };

    const findMenuItemByKey = (foodItemId) => {
        for (const list of Object.values(menuDataByCategory)) {
            const match = (Array.isArray(list) ? list : []).find(
                (row) =>
                    String(row.foodItemId ?? row.id) === String(foodItemId) ||
                    String(row.dailyItemId) === String(foodItemId)
            );
            if (match) return match;
        }
        return menuItems.find(
            (row) =>
                String(row.foodItemId ?? row.Food_Item_Id ?? row.id) === String(foodItemId) ||
                String(row.dailyItemId ?? row.Daily_Item_Id) === String(foodItemId)
        );
    };

    const addToCart = (item) => {
        if (posReadyToPrint || posReadyForNew) {
            return;
        }
        // For offline and catering, orderId comes from backend API; only generate for other types
        if (!currentOrderId && cart.length === 0 && orderTypeFilter !== 'offline' && orderTypeFilter !== 'catering') {
            const newOrderId = Math.floor(Math.random() * 10000);
            setCurrentOrderId(newOrderId);
        }

        const itemKey = getCartItemMatchKey(item);
        if (!itemKey) return;

        // Functional update so rapid clicks merge qty instead of duplicating rows.
        setCart((prev) => {
            const existingItem = prev.find((cartItem) => cartItemsMatch(cartItem, itemKey));
            const nextQty = existingItem
                ? (typeof existingItem.quantity === 'number'
                    ? existingItem.quantity
                    : parseInt(existingItem.quantity, 10) || 0) + 1
                : 1;
            if (!validateOfflineCartQuantity(item, nextQty)) {
                return prev;
            }
            if (existingItem) {
                return prev.map((cartItem) =>
                    cartItemsMatch(cartItem, itemKey)
                        ? {
                              ...cartItem,
                              dailyItemId: cartItem.dailyItemId ?? item.dailyItemId ?? itemKey,
                              foodItemId: cartItem.foodItemId ?? item.foodItemId ?? itemKey,
                              quantity: nextQty,
                              total: parseFloat(cartItem.price || 0) * nextQty,
                          }
                        : cartItem
                );
            }
            return [
                ...prev,
                {
                    ...item,
                    id: item.id || item.foodItemId || itemKey,
                    dailyItemId:
                        item.dailyItemId ?? item.Daily_Item_Id ?? item.foodItemId ?? item.id,
                    foodItemId: item.foodItemId || item.id || itemKey,
                    foodItemName: item.foodItemName || item.name,
                    noOfItemsAvailable:
                        item.noOfItemsAvailable ??
                        item.No_of_items_available ??
                        item.no_of_items_available ??
                        null,
                    quantity: 1,
                    total: parseFloat(item.price || 0),
                },
            ];
        });
    };

    const removeFromCart = (foodItemId) => {
        if (posReadyToPrint || posReadyForNew) return;
        const key = foodItemId == null ? '' : String(foodItemId);
        setCart((prev) => prev.filter((item) => getCartItemMatchKey(item) !== key));
    };

    const updateQuantity = (foodItemId, newQuantity) => {
        if (posReadyToPrint || posReadyForNew) return;
        if (newQuantity <= 0) {
            removeFromCart(foodItemId);
            return;
        }
        const key = foodItemId == null ? '' : String(foodItemId);
        setCart((prev) => {
            const cartItem = prev.find((item) => cartItemsMatch(item, key));
            const stockSource = cartItem || findMenuItemByKey(foodItemId);
            if (!validateOfflineCartQuantity(stockSource || {}, newQuantity)) {
                return prev;
            }
            return prev.map((item) =>
                cartItemsMatch(item, key)
                    ? {
                          ...item,
                          quantity: newQuantity,
                          total: parseFloat(item.price || 0) * newQuantity,
                      }
                    : item
            );
        });
    };

    const calculateTotals = () => {
        const totalQuantity = cart.reduce((sum, item) => {
            const qty = typeof item.quantity === 'number' ? item.quantity : (parseInt(item.quantity) || 0);
            return sum + qty;
        }, 0);

        // Catering uses a negotiated quoted amount, not line-item pricing.
        if (orderTypeFilter === 'catering') {
            const quoted = Math.round(parseFloat(quotedAmount || 0) * 100) / 100;
            const total = Number.isFinite(quoted) && quoted >= 0 ? quoted : 0;
            return { totalQuantity, subtotal: total, gst: 0, total };
        }

        const subtotal = cart.reduce((sum, item) => {
            const qty = typeof item.quantity === 'number' ? item.quantity : (parseInt(item.quantity) || 0);
            return sum + (parseFloat(item.price || 0) * qty);
        }, 0);

        // GST % from Business Profile → Taxes
        const gstPercentage = (manualGst !== '' && !isNaN(parseFloat(manualGst)))
            ? Math.max(0, parseFloat(manualGst))
            : 0;
        const gst = subtotal * (gstPercentage / 100);
        const total = subtotal + gst;
        return { totalQuantity, subtotal, gst, total };
    };

    const clearPosBillForm = () => {
        setCart([]);
        setMobileNumber('');
        setCustomerNameOffline('');
        setTableNumber('');
        setPosOrderType('DINE-IN');
        setPaymentType('');
        setPaymentTypes([]);
        setAdvancePayment('');
        setCurrentOrderId(null);
        setCurrentDisplayOrderNo(null);
        setPaymentSectionExpanded(false);
        setMaximizeCustomerDetailsOpen(false);
        setShowOfflineCustomerDetailsInSidebar(false);
        setOrderIdSearch('');
        setSearchQuery('');
        setShowPosPaymentQr(false);
        setShowPosCardPayment(false);
        setMaximizeRightPane('unpaid');
        setPosCardLast4('');
        setPosCardBankRrn('');
        setPosCardTxnId('');
        setPosUpiSendPhone('');
        setPosUpiSendExpanded(false);
        setPosReadyToPrint(false);
        setPosReadyForNew(false);
        setOrderConfirmationData(null);
        try {
            sessionStorage.removeItem(POS_RESUME_AFTER_PRINT_KEY);
        } catch {
            /* ignore */
        }
    };

    /** Cancel only applies to a saved order — persists CANCELLED (CAN) in DB then clears the form. */
    const handleCancelPosOrder = async () => {
        if (!currentOrderId) return;
        const confirmed = await confirmAction({
            title: 'Cancel order?',
            message: `Cancel order #${
                currentDisplayOrderNo || currentOrderId
            }? This will mark it as cancelled.`,
            confirmLabel: 'Cancel order',
            cancelLabel: 'Keep order',
            danger: true,
        });
        if (!confirmed) return;
        const cancelledId = currentOrderId;
        try {
            if (orderTypeFilter === 'catering') {
                await cancelCateringOrder(cancelledId);
            } else {
                await cancelRestaurantOrder(cancelledId);
            }
            removeOrderFromLocalOrders(cancelledId);
            setUnpaidBills((prev) =>
                prev.filter(
                    (b) => String(b.orderId ?? b.orderNumber) !== String(cancelledId)
                )
            );
            clearPosBillForm();
            window.dispatchEvent(new Event('localOrdersUpdated'));
            await refreshUnpaidBills();
        } catch (err) {
            console.error('Failed to cancel order:', err);
            alert(getApiErrorMessage(err) || 'Failed to cancel order. Please try again.');
        }
    };

    const savePosResumeAfterPrintSnapshot = (snapshot) => {
        try {
            sessionStorage.setItem(POS_RESUME_AFTER_PRINT_KEY, JSON.stringify(snapshot));
        } catch (err) {
            console.warn('Could not save POS bill for resume after print', err);
        }
    };

    const buildPosResumeAfterPrintSnapshot = () => ({
        cart,
        mobileNumber,
        customerNameOffline,
        tableNumber,
        posOrderType,
        paymentType,
        currentOrderId,
        orderConfirmationData,
        showMaximizeModal: true,
        savedAt: Date.now(),
    });

    const openMaximizeUnpaidPane = () => {
        if (!canAccessUnpaidBills()) return;
        setMaximizeRightPane('unpaid');
        setPosUpiSendExpanded(false);
    };

    const openMaximizeQrPane = () => {
        if (!isQrPaymentMethod(paymentType)) {
            setPaymentType('upi');
            setPaymentSectionExpanded(true);
        }
        if (!posUpiSendPhone && mobileNumber) {
            setPosUpiSendPhone(String(mobileNumber).replace(/\D/g, '').slice(-10));
        }
        setShowPosCardPayment(false);
        setShowPosPaymentQr(true);
        setPosUpiSendExpanded(false);
        setMaximizeRightPane('qr');
    };

    const openMaximizeCardPane = () => {
        setShowPosPaymentQr(false);
        setShowPosCardPayment(true);
        setMaximizeRightPane('card');
    };

    const savePosUpiVpaFromDraft = async () => {
        const cleaned = String(posUpiVpaDraft || '').trim();
        writePosUpiVpa(restaurantId, cleaned);
        setPosUpiVpa(cleaned);
        setPosUpiVpaDraft(cleaned);
        if (restaurantId && cleaned) {
            try {
                const saved = await saveRestaurantPosUpiVpa(restaurantId, cleaned, userId);
                if (saved) {
                    writePosUpiVpa(restaurantId, saved);
                    setPosUpiVpa(saved);
                    setPosUpiVpaDraft(saved);
                    return saved;
                }
            } catch (err) {
                console.error('Failed to save restaurant UPI property:', err);
                alert(
                    getApiErrorMessage(
                        err,
                        'Could not save UPI ID to restaurant properties. It is kept on this device only.'
                    )
                );
            }
        }
        return cleaned;
    };

    const selectPosPayMethod = (method) => {
        if (posReadyToPrint || posReadyForNew) return;
        const next = method === 'wallet' ? 'gpay' : method;
        setPaymentType(next);
        setPaymentSectionExpanded(true);
        setShowPosPaymentQr(false);
        setShowPosCardPayment(false);
        if (maximizeRightPane === 'qr' || maximizeRightPane === 'card') {
            setMaximizeRightPane('unpaid');
        }
    };

    const runPosPayment = async () => {
        if (!paymentType) {
            alert('Select Cash, Card, UPI or Wallet first');
            return;
        }
        if (String(paymentType).toLowerCase() === 'card') {
            setShowMaximizeModal(true);
            openMaximizeCardPane();
            return;
        }
        if (isQrPaymentMethod(paymentType)) {
            setShowMaximizeModal(true);
            openMaximizeQrPane();
            return;
        }
        // Cash (and any non-QR, non-card): complete immediately
        await handlePlaceOrder();
        setShowPosPaymentQr(false);
        setShowPosCardPayment(false);
        setMaximizeRightPane('unpaid');
        // Stay on POS — Pay becomes Print (no confirmation popup)
    };

    const confirmPosQrPayment = async () => {
        let vpa = posUpiVpa;
        if (!vpa || (posUpiVpaDraft.trim() && posUpiVpaDraft.trim() !== posUpiVpa)) {
            vpa = await savePosUpiVpaFromDraft();
        }
        if (!vpa) {
            alert('Enter the restaurant UPI ID so customers can pay to your account.');
            return;
        }
        await handlePlaceOrder();
        setShowPosPaymentQr(false);
        setMaximizeRightPane('unpaid');
    };

    const confirmPosCardPayment = async () => {
        const last4 = String(posCardLast4 || '').replace(/\D/g, '');
        if (last4 && last4.length !== 4) {
            alert('Enter the last 4 digits of the card (or leave blank).');
            return;
        }
        await handlePlaceOrder({
            bankRRN: String(posCardBankRrn || '').trim(),
            transactionId:
                String(posCardTxnId || '').trim() ||
                (last4 ? `CARD****${last4}` : undefined),
        });
        setShowPosCardPayment(false);
        setMaximizeRightPane('unpaid');
    };

    const buildUpiShareMessage = () => {
        const amount = Number(
            (() => {
                if (orderTypeFilter === 'catering') {
                    const quoted = parseFloat(quotedAmount);
                    return Number.isFinite(quoted) && quoted > 0 ? quoted : calculateTotals().total;
                }
                return calculateTotals().total;
            })()
        ).toFixed(2);
        const payee =
            restaurantData?.RestaurantName ||
            restaurantData?.restaurantName ||
            'the restaurant';
        const link = buildUpiPayUrl({
            vpa: posUpiVpa || posUpiVpaDraft,
            payeeName: payee,
            amount,
            note: currentOrderId ? `Order ${currentOrderId}` : 'POS bill',
        });
        return `Please pay ₹${amount} to ${payee} via UPI.${link ? ` Tap to pay: ${link}` : ''}`;
    };

    const normalizeIndiaMobile = (raw) => {
        const digits = String(raw || '').replace(/\D/g, '');
        if (digits.length === 10) return digits;
        if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
        if (digits.length > 10) return digits.slice(-10);
        return digits;
    };

    const sendUpiLinkSms = () => {
        const link = buildUpiPayUrl({
            vpa: posUpiVpa || posUpiVpaDraft,
            payeeName:
                restaurantData?.RestaurantName ||
                restaurantData?.restaurantName ||
                'Restaurant',
            amount: calculateTotals().total,
            note: currentOrderId ? `Order ${currentOrderId}` : 'POS bill',
        });
        if (!link) {
            alert('Save the restaurant UPI ID first so the payment link can be generated.');
            return;
        }
        const phone10 = normalizeIndiaMobile(posUpiSendPhone || mobileNumber);
        if (phone10.length !== 10) {
            alert('Enter a valid 10-digit mobile number to send the UPI link.');
            return;
        }
        const body = buildUpiShareMessage();
        const smsTo = `+91${phone10}`;
        const isIos = /iPhone|iPad|iPod/i.test(navigator.userAgent || '');
        const href = isIos
            ? `sms:${smsTo}&body=${encodeURIComponent(body)}`
            : `sms:${smsTo}?body=${encodeURIComponent(body)}`;
        window.location.href = href;
        setPosUpiSendExpanded(false);
    };

    const sendUpiLinkWhatsApp = () => {
        const link = buildUpiPayUrl({
            vpa: posUpiVpa || posUpiVpaDraft,
            payeeName:
                restaurantData?.RestaurantName ||
                restaurantData?.restaurantName ||
                'Restaurant',
            amount: calculateTotals().total,
            note: currentOrderId ? `Order ${currentOrderId}` : 'POS bill',
        });
        if (!link) {
            alert('Save the restaurant UPI ID first so the payment link can be generated.');
            return;
        }
        const phone10 = normalizeIndiaMobile(posUpiSendPhone || mobileNumber);
        if (phone10.length !== 10) {
            alert('Enter a valid 10-digit mobile number to send the UPI link.');
            return;
        }
        const text = buildUpiShareMessage();
        window.open(
            `https://wa.me/91${phone10}?text=${encodeURIComponent(text)}`,
            '_blank',
            'noopener,noreferrer'
        );
        setPosUpiSendExpanded(false);
    };

    const handleSaveOrder = async () => {
        if (cart.length === 0) {
            alert('Please add items to cart');
            return;
        }
        if (orderTypeFilter === 'catering') {
            const quoted = parseFloat(quotedAmount);
            if (!Number.isFinite(quoted) || quoted <= 0) {
                alert('Please enter a quoted amount');
                return;
            }
        }

        // Calculate totals
        const { subtotal, gst, total } = calculateTotals();
        
        // Calculate advance payment and pending amount
        // Round to 2 decimal places to avoid floating point precision issues
        const advancePaymentValue = advancePayment ? Math.round(parseFloat(advancePayment) * 100) / 100 : 0;
        const totalRounded = Math.round(total * 100) / 100;
        const pendingAmount = Math.max(0, Math.round((totalRounded - advancePaymentValue) * 100) / 100);
        
        // Ensure tableNumber is preserved correctly
        const savedTableNumber = tableNumber && tableNumber.toString().trim() !== '' ? tableNumber.toString() : null;
        
        const localOrders = JSON.parse(localStorage.getItem('localOrders') || '[]');
        
        // Offline/catering: orderId from API when creating new; other: use currentOrderId or generate
        let orderId = orderTypeFilter === 'offline' ? (currentOrderId ?? null)
            : orderTypeFilter === 'catering' ? (currentOrderId ?? null)
            : (currentOrderId || Math.floor(Math.random() * 10000));
        let firstOfflineResult = null;
        let firstCateringResult = null;

        // Offline: create order via API (single request with lineItems) when order is new
        const isNewOfflineOrder = currentOrderId == null;
        if (orderTypeFilter === 'offline' && restaurantId && userId && isNewOfflineOrder) {
            try {
                const data = await createOfflineOrderViaApi({
                    customerName: customerNameOffline || 'N/A',
                    customerPhone: mobileNumber || '',
                    tableNumber: savedTableNumber,
                    posOrderType,
                    cart,
                    orderStatus: 'NEW',
                    gst,
                });
                const backendOrderId = data?.orderId ?? data?.order_id;
                if (backendOrderId != null) {
                    firstOfflineResult = data;
                    orderId = backendOrderId;
                    setCurrentDisplayOrderNo(
                        resolveDisplayOrderNo(data, String(backendOrderId))
                    );
                    const createdLineIds =
                        data.orderLineItemIds ??
                        data.order_line_item_ids ??
                        [];
                    if (Array.isArray(createdLineIds) && createdLineIds.length > 0) {
                        setCart((prev) =>
                            prev.map((item, idx) =>
                                createdLineIds[idx] != null
                                    ? { ...item, offlineLineItemId: createdLineIds[idx] }
                                    : item
                            )
                        );
                    }
                }
            } catch (err) {
                console.error('Create offline order API error:', err?.response?.data || err);
                alert(
                    getApiErrorMessage(err, 'POS order could not be created on server.')
                );
                return;
            }
            if (orderId == null || orderId === undefined) {
                alert('Could not create order. Please try again.');
                return;
            }
        }

        const posTotalFromApi =
            firstOfflineResult?.totalPrice != null
                ? parseFloat(firstOfflineResult.totalPrice)
                : null;
        const orderTotal =
            posTotalFromApi != null && !Number.isNaN(posTotalFromApi) ? posTotalFromApi : total;

        // Catering: create only when there is no existing order id (API-loaded edits are not in localStorage).
        const isNewCateringOrder = currentOrderId == null;
        if (orderTypeFilter === 'catering' && restaurantId && userId && isNewCateringOrder) {
            try {
                const data = await createCateringOrderViaApi({
                    customerName: customerName || 'N/A',
                    contactNumber: contactNumber || null,
                    altNumber: altNumber || null,
                    eventDate: eventDate || null,
                    eventLocation: eventLocation || null,
                    numberOfGuests: numberOfGuests || null,
                    orderStatus: 'NEW',
                    totalPrice: orderTotal,
                    cart,
                });
                const backendOrderId = data != null ? (data.cateringOrderId ?? data.orderId ?? data.order_id) : null;
                if (backendOrderId != null) {
                    firstCateringResult = data;
                    orderId = backendOrderId;
                }
            } catch (err) {
                console.error('Create catering order API error:', err?.response?.data || err);
                alert(getApiErrorMessage(err, 'Catering order could not be created on server.'));
                return;
            }
            if (orderId == null || orderId === undefined) {
                alert('Could not create order. Please try again.');
                return;
            }
        }
        // Use the same orderId as orderNumber for consistency
        const orderNumber = orderId;
        
        // Check if order already exists in localStorage
        const orderIndex = localOrders.findIndex(
            (order) => String(order.orderId) === String(orderId)
        );
        
        if (orderIndex !== -1 || ((orderTypeFilter === 'catering' || orderTypeFilter === 'offline') && currentOrderId != null)) {
            // Update existing order - keep same orderId and orderNumber
            const existingOrder = orderIndex !== -1 ? localOrders[orderIndex] : {};
            const baseOrderData = {
                ...existingOrder,
                orderId: orderId,
                orderNumber: orderNumber ?? orderId,
                restaurantId: restaurantId,
                paymentMethod: orderTypeFilter === 'catering' 
                    ? (paymentTypes.length > 0 ? paymentTypes : (existingOrder.paymentMethod || null))
                    : (paymentType || null),
                totalPrice: orderTotal,
                advancePayment: advancePaymentValue > 0 ? parseFloat(advancePaymentValue.toFixed(2)) : null,
                advancePaymentMethod: orderTypeFilter === 'catering' && advancePaymentValue > 0 && advancePaymentMethod ? advancePaymentMethod : null,
                pendingAmount: advancePaymentValue > 0 ? parseFloat(pendingAmount.toFixed(2)) : null,
                orderStatus: 'NEW', // Keep status as NEW for saved orders
                orderLineItems: cart.map(item => ({
                    foodItemId: item.foodItemId,
                    foodItemName: item.foodItemName || item.name,
                    foodQuantity: typeof item.quantity === 'number' ? item.quantity : (parseInt(item.quantity) || 1),
                    price: parseFloat(item.price || 0),
                    ...(item.offlineLineItemId != null && { offlineLineItemId: item.offlineLineItemId }),
                }))
            };
            
            // Add catering or offline order fields
            if (orderTypeFilter === 'catering') {
                baseOrderData.orderType = 'catering';
                baseOrderData.customerName = customerName || 'N/A';
                baseOrderData.mobile = contactNumber || null;
                baseOrderData.customerMobile = contactNumber || null;
                baseOrderData.contactNumber = contactNumber || null;
                baseOrderData.altNumber = altNumber || null;
                baseOrderData.eventDate = eventDate || null;
                baseOrderData.eventLocation = eventLocation || null;
                baseOrderData.numberOfGuests = numberOfGuests ? parseInt(numberOfGuests) : null;
            } else {
                baseOrderData.customerName = customerNameOffline || (mobileNumber ? `Customer-${mobileNumber}` : 'N/A');
                baseOrderData.mobile = mobileNumber || null;
                baseOrderData.tableNumber = savedTableNumber;
                baseOrderData.orderType = posOrderType;
            }
            
            // Catering: persist full order data to backend when saving existing catering order
            if (orderTypeFilter === 'catering' && orderId && userId) {
                try {
                    await updateCateringOrderViaApi(orderId, {
                        orderStatus: 'NEW',
                        deliveryContactName: customerName || null,
                        deliveryContactNumber: contactNumber || null,
                        altNumber: altNumber || null,
                        updatedBy: userId ? Number(userId) : 1,
                        customerName: customerName || null,
                        contactNumber: contactNumber || null,
                        numberOfGuests: numberOfGuests || null,
                        eventLocation: eventLocation || null,
                        totalPrice: orderTotal,
                        cart,
                    });
                } catch (err) {
                    console.error('Update catering order API error:', err?.response?.data || err);
                    alert('Catering order could not be updated on server. Changes saved locally.');
                }
            }
            // Offline: update contact + append any newly added line items on the server
            if (orderTypeFilter === 'offline' && orderId && userId && cart.length > 0) {
                try {
                    await updatePosOrderViaApi(orderId, {
                        customerName: customerNameOffline || 'N/A',
                        customerPhone: mobileNumber || '',
                        posOrderType,
                        orderStatus: 'NEW',
                    });
                    const newCartItems = cart.filter((item) => item.offlineLineItemId == null);
                    const newLineItems = buildPosOrderLineItems(newCartItems);
                    if (newLineItems.length > 0 && normalizePosOrderType(posOrderType) === 'DINE-IN') {
                        const updated = await updateDineInOrder(orderId, {
                            lineItems: newLineItems,
                            tableNo: formatPosTableNo(savedTableNumber),
                            updatedById: userId ? Number(userId) : 1,
                        });
                        const addedIds = updated.added_line_item_ids || updated.addedLineItemIds || [];
                        if (Array.isArray(addedIds) && addedIds.length > 0) {
                            let addIdx = 0;
                            setCart((prev) =>
                                prev.map((item) => {
                                    if (item.offlineLineItemId != null) return item;
                                    const assigned = addedIds[addIdx];
                                    addIdx += 1;
                                    return assigned != null
                                        ? { ...item, offlineLineItemId: assigned }
                                        : item;
                                })
                            );
                        }
                        if (updated.total_price != null || updated.totalPrice != null) {
                            baseOrderData.totalPrice = parseFloat(
                                updated.total_price ?? updated.totalPrice
                            );
                        }
                    }
                } catch (err) {
                    console.error('Update dine-in order API error:', err?.response?.data || err);
                    alert('POS order could not be updated on server. Changes saved locally.');
                }
            }
            
            if (orderIndex !== -1) {
                localOrders[orderIndex] = baseOrderData;
            } else {
                if (!baseOrderData.createdDate) {
                    baseOrderData.createdDate = new Date().toISOString();
                }
                localOrders.push(baseOrderData);
            }
            localStorage.setItem('localOrders', JSON.stringify(localOrders));
            window.dispatchEvent(new Event('localOrdersUpdated'));
            alert('Order saved successfully!');
        } else {
            // Create new order (orderId from API for offline/catering, or generated for others)
            const baseOrderData = {
                orderId: orderId,
                orderNumber: orderNumber,
                ...(() => {
                    const displayNo = pickApiDisplayOrderNo(
                        firstOfflineResult,
                        firstCateringResult
                    );
                    return displayNo
                        ? { billNo: displayNo, displayOrderNo: displayNo }
                        : {};
                })(),
                restaurantId: restaurantId,
                paymentMethod: orderTypeFilter === 'catering' 
                    ? (paymentTypes.length > 0 ? paymentTypes : null)
                    : (paymentType || null),
                totalPrice: orderTotal,
                advancePayment: advancePaymentValue > 0 ? parseFloat(advancePaymentValue.toFixed(2)) : null,
                advancePaymentMethod: orderTypeFilter === 'catering' && advancePaymentValue > 0 && advancePaymentMethod ? advancePaymentMethod : null,
                pendingAmount: advancePaymentValue > 0 ? parseFloat(pendingAmount.toFixed(2)) : null,
                orderStatus: 'NEW', // Status for saved orders - appears in Save Order table
                createdDate: new Date().toISOString(),
                orderLineItems: cart.map(item => ({
                    foodItemId: item.foodItemId,
                    foodItemName: item.foodItemName || item.name,
                    foodQuantity: typeof item.quantity === 'number' ? item.quantity : (parseInt(item.quantity) || 1),
                    price: parseFloat(item.price || 0)
                }))
            };
            
            // Add catering or offline order fields
            if (orderTypeFilter === 'catering') {
                baseOrderData.orderType = 'catering';
                baseOrderData.customerName = customerName || 'N/A';
                baseOrderData.mobile = contactNumber || null;
                baseOrderData.customerMobile = contactNumber || null;
                baseOrderData.contactNumber = contactNumber || null;
                baseOrderData.altNumber = altNumber || null;
                baseOrderData.eventDate = eventDate || null;
                baseOrderData.eventLocation = eventLocation || null;
                baseOrderData.numberOfGuests = numberOfGuests ? parseInt(numberOfGuests) : null;
            } else {
                baseOrderData.customerName = customerNameOffline || (mobileNumber ? `Customer-${mobileNumber}` : 'N/A');
                baseOrderData.mobile = mobileNumber || null;
                baseOrderData.tableNumber = savedTableNumber;
                baseOrderData.orderType = posOrderType;
            }

            localOrders.push(baseOrderData);
            localStorage.setItem('localOrders', JSON.stringify(localOrders));
            if (!currentOrderId) {
                setCurrentOrderId(orderId); // Set current order ID after creating
                setCurrentDisplayOrderNo((prev) =>
                    prev || resolveDisplayOrderNo(firstOfflineResult, String(orderId))
                );
            }
            alert('Order saved successfully!');
        }
        
        // Dine-in / POS: stay on POS, refresh unpaid list, and clear form for the next bill.
        // Staff can tap an unpaid bill to continue adding items.
        if (orderTypeFilter === 'offline') {
            await refreshUnpaidBills();
            clearPosBillForm();
            return;
        }

        const orderType =
            orderTypeFilter === 'catering'
                ? 'catering'
                : orderTypeFilter === 'jreats'
                    ? 'jreats'
                    : 'myeats';
        navigate(`/home?tab=orders&orderType=${orderType}`, {
            replace: true,
            state: {},
        });
    };

    const handlePlaceOrder = async (paymentExtras = {}) => {
        if (cart.length === 0) {
            alert('Please add items to cart');
            return;
        }
        if (orderTypeFilter === 'catering') {
            const quoted = parseFloat(quotedAmount);
            if (!Number.isFinite(quoted) || quoted <= 0) {
                alert('Please enter a quoted amount');
                return;
            }
        }
        // Payment type is mandatory
        if (orderTypeFilter === 'catering') {
            if (!paymentTypes || paymentTypes.length === 0) {
                alert('Please select at least one payment type');
                return;
            }
        } else {
            if (!paymentType || paymentType.trim() === '') {
                alert('Please select a payment type');
                return;
            }
        }
        const selectedPaymentType = orderTypeFilter === 'catering' ? paymentTypes : paymentType;

        // Calculate totals
        const { subtotal, gst, total } = calculateTotals();
        
        // Calculate advance payment and pending amount
        // Round to 2 decimal places to avoid floating point precision issues
        const advancePaymentValue = advancePayment ? Math.round(parseFloat(advancePayment) * 100) / 100 : 0;
        const totalRounded = Math.round(total * 100) / 100;
        const pendingAmount = Math.max(0, Math.round((totalRounded - advancePaymentValue) * 100) / 100);
        
        // Ensure tableNumber is preserved correctly - convert to string if it's a number, keep as is if string
        const savedTableNumber = tableNumber && tableNumber.toString().trim() !== '' ? tableNumber.toString() : null;
        
        const localOrders = JSON.parse(localStorage.getItem('localOrders') || '[]');
        let orderId, orderNumber, orderData;
        let firstOfflineResult = null;
        let firstCateringResult = null;

        // Offline: create order via API (single request with lineItems) when order is new
        const isNewOfflineOrderPlace = currentOrderId == null;
        if (orderTypeFilter === 'offline' && restaurantId && userId && isNewOfflineOrderPlace) {
            try {
                const data = await createOfflineOrderViaApi({
                    customerName: customerNameOffline || 'N/A',
                    customerPhone: mobileNumber || '',
                    tableNumber: savedTableNumber,
                    posOrderType,
                    cart,
                    orderStatus: 'COMPLETED',
                    gst,
                });
                const backendOrderId = data?.orderId ?? data?.order_id;
                if (backendOrderId != null) {
                    firstOfflineResult = data;
                    orderId = backendOrderId;
                    orderNumber = orderId;
                    setCurrentDisplayOrderNo(
                        resolveDisplayOrderNo(data, String(backendOrderId))
                    );
                }
            } catch (err) {
                console.error('Create offline order API error:', err?.response?.data || err);
                alert(
                    getApiErrorMessage(err, 'POS order could not be created on server.')
                );
                return;
            }
            if (orderId == null || orderId === undefined) {
                alert('Could not create order. Please try again.');
                return;
            }
        }

        const posTotalFromApiPlace =
            firstOfflineResult?.totalPrice != null
                ? parseFloat(firstOfflineResult.totalPrice)
                : null;
        const orderTotalPlace =
            posTotalFromApiPlace != null && !Number.isNaN(posTotalFromApiPlace)
                ? posTotalFromApiPlace
                : total;

        // Catering: create only when there is no existing order id (API-loaded edits are not in localStorage).
        const isNewCateringOrderPlace = currentOrderId == null;
        if (orderTypeFilter === 'catering' && restaurantId && userId && isNewCateringOrderPlace) {
            try {
                const data = await createCateringOrderViaApi({
                    customerName: customerName || 'N/A',
                    contactNumber: contactNumber || null,
                    altNumber: altNumber || null,
                    eventDate: eventDate || null,
                    eventLocation: eventLocation || null,
                    numberOfGuests: numberOfGuests || null,
                    orderStatus: 'COMPLETED',
                    totalPrice: orderTotalPlace,
                    cart,
                });
                const backendOrderId = data != null ? (data.cateringOrderId ?? data.orderId ?? data.order_id) : null;
                if (backendOrderId != null) {
                    firstCateringResult = data;
                    orderId = backendOrderId;
                    orderNumber = orderId;
                }
            } catch (err) {
                console.error('Create catering order API error:', err?.response?.data || err);
                alert(getApiErrorMessage(err, 'Catering order could not be created on server.'));
                return;
            }
            if (orderId == null || orderId === undefined) {
                alert('Could not create order. Please try again.');
                return;
            }
        }

        if (currentOrderId) {
            // Update existing order
            const orderIndex = localOrders.findIndex(
                (order) => String(order.orderId) === String(currentOrderId)
            );
            if (orderIndex !== -1) {
                const existingOrder = localOrders[orderIndex];
                orderId = existingOrder.orderId;
                orderNumber = existingOrder.orderNumber;
                
                // Update existing order - keep same orderId and orderNumber
                const baseOrderData = {
                    ...existingOrder,
                    restaurantId: restaurantId,
                    paymentMethod: orderTypeFilter === 'catering' 
                        ? (Array.isArray(selectedPaymentType) ? selectedPaymentType : [selectedPaymentType])
                        : selectedPaymentType,
                    totalPrice: orderTotalPlace,
                    advancePayment: advancePaymentValue > 0 ? parseFloat(advancePaymentValue.toFixed(2)) : null,
                    advancePaymentMethod: advancePaymentValue > 0 && advancePaymentMethod ? advancePaymentMethod : null,
                    pendingAmount: advancePaymentValue > 0 ? parseFloat(pendingAmount.toFixed(2)) : null,
                    orderStatus: orderTypeFilter === 'catering' ? 'COMPLETED' : 'COMPLETED',
                    orderLineItems: cart.map(item => ({
                        foodItemId: item.foodItemId,
                        foodItemName: item.foodItemName || item.name,
                        foodQuantity: typeof item.quantity === 'number' ? item.quantity : (parseInt(item.quantity) || 1),
                        price: parseFloat(item.price || 0),
                        ...(item.offlineLineItemId != null && { offlineLineItemId: item.offlineLineItemId }),
                    }))
                };
                
                // Add catering or offline order fields
                if (orderTypeFilter === 'catering') {
                    baseOrderData.orderType = 'catering';
                    baseOrderData.customerName = customerName || 'N/A';
                    baseOrderData.mobile = contactNumber || null;
                    baseOrderData.customerMobile = contactNumber || null;
                    baseOrderData.contactNumber = contactNumber || null;
                    baseOrderData.altNumber = altNumber || null;
                    baseOrderData.eventDate = eventDate || null;
                    baseOrderData.eventLocation = eventLocation || null;
                    baseOrderData.numberOfGuests = numberOfGuests ? parseInt(numberOfGuests) : null;
                } else {
                    baseOrderData.customerName = mobileNumber ? `Customer-${mobileNumber}` : 'N/A';
                    baseOrderData.mobile = mobileNumber || null;
                    baseOrderData.tableNumber = savedTableNumber;
                    baseOrderData.orderType = posOrderType;
                }
                
                orderData = baseOrderData;
                localOrders[orderIndex] = orderData;
                // Do NOT mark COMPLETED here — line-item sync must run while order is still NEW.
                // Status + payment are handled in the DINE-IN complete block below.
                if (orderTypeFilter === 'catering' && currentOrderId && userId) {
                    try {
                        await updateCateringOrderViaApi(currentOrderId, {
                            orderStatus: 'COMPLETED',
                            deliveryContactName: customerName || null,
                            deliveryContactNumber: contactNumber || null,
                            altNumber: altNumber || null,
                            updatedBy: userId ? Number(userId) : 1,
                            customerName: customerName || null,
                            contactNumber: contactNumber || null,
                            numberOfGuests: numberOfGuests || null,
                            eventLocation: eventLocation || null,
                            totalPrice: orderTotalPlace,
                            cart,
                        });
                    } catch (err) {
                        console.error('Update catering order API error:', err?.response?.data || err);
                    }
                }
            } else {
                // Existing order id (e.g. API-loaded catering) not in localStorage — update server, then cache locally
                orderId = orderId ?? currentOrderId ?? (orderTypeFilter === 'offline' || orderTypeFilter === 'catering' ? null : Math.floor(Math.random() * 10000));
                orderNumber = orderNumber ?? orderId;
                const baseOrderData = {
                    orderId: orderId,
                    orderNumber: orderNumber,
                    ...(() => {
                        const displayNo = pickApiDisplayOrderNo(
                            firstOfflineResult,
                            firstCateringResult
                        );
                        return displayNo
                            ? { billNo: displayNo, displayOrderNo: displayNo }
                            : {};
                    })(),
                    restaurantId: restaurantId,
                    paymentMethod: orderTypeFilter === 'catering' 
                        ? (Array.isArray(selectedPaymentType) ? selectedPaymentType : [selectedPaymentType])
                        : selectedPaymentType,
                    totalPrice: orderTotalPlace,
                    advancePayment: advancePaymentValue > 0 ? parseFloat(advancePaymentValue.toFixed(2)) : null,
                    advancePaymentMethod: advancePaymentValue > 0 && advancePaymentMethod ? advancePaymentMethod : null,
                    pendingAmount: advancePaymentValue > 0 ? parseFloat(pendingAmount.toFixed(2)) : null,
                    orderStatus: orderTypeFilter === 'catering' ? 'COMPLETED' : 'COMPLETED',
                    createdDate: new Date().toISOString(),
                    orderLineItems: cart.map(item => ({
                        foodItemId: item.foodItemId,
                        foodItemName: item.foodItemName || item.name,
                        foodQuantity: typeof item.quantity === 'number' ? item.quantity : (parseInt(item.quantity) || 1),
                        price: parseFloat(item.price || 0)
                    }))
                };
                
                // Add catering or offline order fields
                if (orderTypeFilter === 'catering') {
                    baseOrderData.orderType = 'catering';
                    baseOrderData.customerName = customerName || 'N/A';
                    baseOrderData.mobile = contactNumber || null;
                    baseOrderData.customerMobile = contactNumber || null;
                    baseOrderData.contactNumber = contactNumber || null;
                    baseOrderData.altNumber = altNumber || null;
                    baseOrderData.eventDate = eventDate || null;
                    baseOrderData.eventLocation = eventLocation || null;
                    baseOrderData.numberOfGuests = numberOfGuests ? parseInt(numberOfGuests) : null;
                } else {
                    baseOrderData.customerName = mobileNumber ? `Customer-${mobileNumber}` : 'N/A';
                    baseOrderData.mobile = mobileNumber || null;
                    baseOrderData.tableNumber = savedTableNumber;
                    baseOrderData.orderType = posOrderType;
                }

                if (orderTypeFilter === 'catering' && currentOrderId && userId) {
                    try {
                        await updateCateringOrderViaApi(currentOrderId, {
                            orderStatus: 'COMPLETED',
                            deliveryContactName: customerName || null,
                            deliveryContactNumber: contactNumber || null,
                            altNumber: altNumber || null,
                            updatedBy: userId ? Number(userId) : 1,
                            customerName: customerName || null,
                            contactNumber: contactNumber || null,
                            numberOfGuests: numberOfGuests || null,
                            eventLocation: eventLocation || null,
                            totalPrice: orderTotalPlace,
                            cart,
                        });
                    } catch (err) {
                        console.error('Update catering order API error:', err?.response?.data || err);
                    }
                }
                
                orderData = baseOrderData;
                localOrders.push(orderData);
            }
        } else {
            // Create new order - use API orderId or currentOrderId (offline/catering use API only, no random)
            orderId = orderId ?? currentOrderId ?? (orderTypeFilter === 'offline' || orderTypeFilter === 'catering' ? null : Math.floor(Math.random() * 10000));
            orderNumber = orderNumber ?? orderId;
            const baseOrderData = {
                orderId: orderId,
                orderNumber: orderNumber,
                ...(() => {
                    const displayNo = pickApiDisplayOrderNo(
                        firstOfflineResult,
                        firstCateringResult
                    );
                    return displayNo
                        ? { billNo: displayNo, displayOrderNo: displayNo }
                        : {};
                })(),
                restaurantId: restaurantId,
                paymentMethod: orderTypeFilter === 'catering' 
                    ? (Array.isArray(selectedPaymentType) ? selectedPaymentType : [selectedPaymentType])
                    : selectedPaymentType,
                totalPrice: orderTotalPlace,
                advancePayment: advancePaymentValue > 0 ? parseFloat(advancePaymentValue.toFixed(2)) : null,
                advancePaymentMethod: orderTypeFilter === 'catering' && advancePaymentValue > 0 && advancePaymentMethod ? advancePaymentMethod : null,
                pendingAmount: advancePaymentValue > 0 ? parseFloat(pendingAmount.toFixed(2)) : null,
                orderStatus: orderTypeFilter === 'catering' ? 'NEW' : 'COMPLETED',
                createdDate: new Date().toISOString(),
                orderLineItems: cart.map(item => ({
                    foodItemId: item.foodItemId,
                    foodItemName: item.foodItemName || item.name,
                    foodQuantity: typeof item.quantity === 'number' ? item.quantity : (parseInt(item.quantity) || 1),
                    price: parseFloat(item.price || 0)
                }))
            };
            
            // Add catering or offline order fields
            if (orderTypeFilter === 'catering') {
                baseOrderData.orderType = 'catering';
                baseOrderData.customerName = customerName || 'N/A';
                baseOrderData.mobile = contactNumber || null;
                baseOrderData.customerMobile = contactNumber || null;
                baseOrderData.contactNumber = contactNumber || null;
                baseOrderData.altNumber = altNumber || null;
                baseOrderData.eventDate = eventDate || null;
                baseOrderData.eventLocation = eventLocation || null;
                baseOrderData.numberOfGuests = numberOfGuests ? parseInt(numberOfGuests) : null;
            } else {
                baseOrderData.customerName = customerNameOffline || (mobileNumber ? `Customer-${mobileNumber}` : 'N/A');
                baseOrderData.mobile = mobileNumber || null;
                baseOrderData.tableNumber = savedTableNumber;
                baseOrderData.orderType = posOrderType;
            }
            
            orderData = baseOrderData;
            localOrders.push(orderData);
        }

        if (orderTypeFilter === 'offline' && orderId == null && currentOrderId != null) {
            orderId = currentOrderId;
        }

        // DINE-IN / POS Complete:
        // 1) append any new lines while order is still NEW
        // 2) record payment
        // 3) mark COMPLETED (after lines — API rejects line edits on COMPLETED)
        if (orderTypeFilter === 'offline' && orderId != null && restaurantId && userId) {
            const custName =
                customerNameOffline ||
                (mobileNumber ? `Customer-${mobileNumber}` : 'N/A');
            const paymentAmount = orderTotalPlace ?? totalRounded;
            const isCompletedModifyError = (err) =>
                /cannot modify order with status\s*completed/i.test(
                    getApiErrorMessage(err, '') || ''
                );

            try {
                const newCartItems = cart.filter((item) => item.offlineLineItemId == null);
                const newLineItems = buildPosOrderLineItems(newCartItems);
                if (newLineItems.length > 0 && normalizePosOrderType(posOrderType) === 'DINE-IN') {
                    try {
                        await updateDineInOrder(orderId, {
                            lineItems: newLineItems,
                            tableNo: formatPosTableNo(savedTableNumber),
                            updatedById: userId ? Number(userId) : 1,
                        });
                    } catch (lineErr) {
                        // Order may already be COMPLETED from a prior Pay attempt — skip re-adding lines.
                        if (!isCompletedModifyError(lineErr)) throw lineErr;
                        console.warn(
                            'Skipping line-item sync; order already completed:',
                            orderId
                        );
                    }
                }
                await createPosPaymentViaApi(
                    orderId,
                    paymentAmount,
                    custName,
                    selectedPaymentType,
                    {
                        bankRRN: paymentExtras?.bankRRN,
                        transactionId: paymentExtras?.transactionId,
                    }
                );
                if (normalizePosOrderType(posOrderType) === 'DINE-IN') {
                    try {
                        await updateDineInOrder(orderId, {
                            orderStatus: 'COMPLETED',
                            orderStatusCode: 'CMP',
                            tableNo: formatPosTableNo(savedTableNumber),
                            updatedById: userId ? Number(userId) : 1,
                        });
                    } catch (statusErr) {
                        if (!isCompletedModifyError(statusErr)) throw statusErr;
                    }
                } else {
                    await updatePosOrderViaApi(orderId, {
                        customerName: custName,
                        customerPhone: mobileNumber || '',
                        posOrderType,
                        orderStatus: 'COMPLETED',
                    });
                }
            } catch (err) {
                console.error('Create payment API error:', err?.response?.data || err);
                const apiMsg = getApiErrorMessage(
                    err,
                    'Order was saved but payment could not be recorded. Please try again from Orders.'
                );
                alert(apiMsg);
                return;
            }
        }
        
        localStorage.setItem('localOrders', JSON.stringify(localOrders));
        
        // Dispatch custom event to notify other components that orders have been updated
        window.dispatchEvent(new Event('localOrdersUpdated'));

        // New order placed = we created via API in this flow; existing = we updated an existing order
        const isNewOrderPlaced = !!(firstOfflineResult || firstCateringResult);
        const receiptDisplayNo =
            pickApiDisplayOrderNo(
                firstOfflineResult,
                firstCateringResult,
                orderData,
                { displayOrderNo: currentDisplayOrderNo, orderId }
            ) || currentDisplayOrderNo || null;
        // Prepare order confirmation data — Bill Num uses display order number
        const confirmationData = {
            orderId: orderId,
            orderNumber: orderNumber,
            billNo: receiptDisplayNo,
            displayOrderNo: receiptDisplayNo,
            mobile: orderTypeFilter === 'catering' ? (contactNumber || null) : (mobileNumber || null),
            tableNumber: orderTypeFilter === 'catering' ? null : savedTableNumber,
            paymentMethod: orderTypeFilter === 'catering' 
                ? (Array.isArray(selectedPaymentType) ? selectedPaymentType : [selectedPaymentType])
                : selectedPaymentType,
            restaurantId: restaurantId,
            items: cart.map(item => ({
                foodItemName: item.foodItemName || item.name,
                foodQuantity: typeof item.quantity === 'number' ? item.quantity : (parseInt(item.quantity) || 1),
                quantity: typeof item.quantity === 'number' ? item.quantity : (parseInt(item.quantity) || 1),
                price: parseFloat(item.price || 0)
            })),
            subtotal: subtotal,
            gst: gst,
            gstPercentage: subtotal > 0 ? Math.round((gst / subtotal) * 1000) / 10 : 0,
            total: total,
            // Catering fields
            orderType: orderTypeFilter === 'catering' ? 'catering' : posOrderType,
            customerName: orderTypeFilter === 'catering' ? customerName : null,
            contactNumber: orderTypeFilter === 'catering' ? contactNumber : null,
            eventDate: orderTypeFilter === 'catering' ? eventDate : null,
            eventLocation: orderTypeFilter === 'catering' ? eventLocation : null,
            numberOfGuests: orderTypeFilter === 'catering' ? (numberOfGuests ? parseInt(numberOfGuests) : null) : null,
            isNewOrderPlaced,
        };

        if (orderTypeFilter === 'offline') {
            if (orderId != null) {
                setCurrentOrderId(orderId);
                setCurrentDisplayOrderNo((prev) =>
                    prev ||
                    resolveDisplayOrderNo(firstOfflineResult, String(orderId))
                );
            }
            refreshUnpaidBills();
            setOrderConfirmationData(confirmationData);
            setPosReadyToPrint(true);
            setPosReadyForNew(false);
            setShowPosPaymentQr(false);
            setShowPosCardPayment(false);
            setMaximizeRightPane('unpaid');
            setPosCardLast4('');
            setPosCardBankRrn('');
            setPosCardTxnId('');
            setPosUpiSendPhone('');
            setPosUpiSendExpanded(false);
            // Keep cart + order details on screen; Pay becomes Print
            return;
        }

        // Clear cart and form (non-POS flows)
        setCart([]);
        setMobileNumber('');
        setCustomerNameOffline('');
        setTableNumber('');
        setPosOrderType('DINE-IN');
        setPaymentType('');
        setPaymentTypes([]);
        setAdvancePayment('');
        setCurrentOrderId(null);
        setCurrentDisplayOrderNo(null);
        setPaymentSectionExpanded(false);
        setOrderIdSearch('');
        setShowPosPaymentQr(false);
        setShowPosCardPayment(false);
        setMaximizeRightPane('unpaid');
        setPosCardLast4('');
        setPosCardBankRrn('');
        setPosCardTxnId('');
        setPosUpiSendPhone('');
        setPosUpiSendExpanded(false);
        if (orderTypeFilter === 'catering') {
            setAdvancePaymentMethod('');
        }
        if (orderTypeFilter === 'catering') {
                setCustomerName('');
            setContactNumber('');
            setAltNumber('');
            setEventDate('');
            setEventLocation('');
            setNumberOfGuests('');
        }
        setIsCartOpen(false);

        // Show order confirmation popup (non-POS flows)
        setOrderConfirmationData(confirmationData);
        setShowOrderConfirmation(true);
    };

    const getRestaurantContact = () => {
        if (!restaurantData) {
            return {
                name: 'N/A',
                phone: 'N/A',
                email: 'N/A',
                address: 'N/A'
            };
        }
        
        const formatAddress = () => {
            const addr = restaurantData.Address;
            if (!addr) return 'N/A';
            if (typeof addr === 'string') return addr;
            
            let addressObj = addr;
            if (Array.isArray(addr) && addr.length > 0) {
                addressObj = addr[0];
            }
            
            const parts = [
                addressObj.AddressLine1 || addressObj.addressLine1,
                addressObj.AddressLine2 || addressObj.addressLine2,
                addressObj.City || addressObj.city,
                addressObj.State || addressObj.state,
                addressObj.PostalCode || addressObj.postalCode,
                addressObj.Country || addressObj.country
            ].filter(Boolean);
            
            return parts.length > 0 ? parts.join(', ') : 'N/A';
        };
        
        return {
            name: restaurantData.RestaurantName || 'N/A',
            phone: restaurantData.ContactNo || 'N/A',
            email: restaurantData.Email || 'N/A',
            address: formatAddress()
        };
    };


    const { totalQuantity, subtotal, gst, total } = calculateTotals();
    const gstPercentLabel = (() => {
        const n = parseFloat(manualGst);
        if (!Number.isFinite(n) || n < 0) return '0';
        return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
    })();

    const posPayAmount = (() => {
        if (orderTypeFilter === 'catering') {
            const quoted = parseFloat(quotedAmount);
            return Number.isFinite(quoted) && quoted > 0 ? quoted : total;
        }
        return total;
    })();

    const posUpiPayUrl = buildUpiPayUrl({
        vpa: posUpiVpa || posUpiVpaDraft,
        payeeName: restaurantData?.RestaurantName || restaurantData?.restaurantName || 'Restaurant',
        amount: posPayAmount,
        note: currentOrderId ? `Order ${currentOrderId}` : 'POS bill',
    });
    const posUpiQrImageUrl = buildQrImageUrl(posUpiPayUrl);

    const handlePosOrderTypeChange = (e) => {
        const next = e.target.value;
        setPosOrderType(next);
        if (next !== 'DINE-IN') {
            setTableNumber('');
        }
    };

    const renderPosOrderTypeSelect = (className = 'pos-order-type-select') => (
        <select
            className={className}
            value={posOrderType}
            onChange={handlePosOrderTypeChange}
            aria-label="Order type"
        >
            {POS_ORDER_TYPES.map((type) => (
                <option key={type} value={type}>
                    {getPosOrderTypeLabel(type)}
                </option>
            ))}
        </select>
    );

    const renderTableNumberInput = (className = 'pos-table-input') => {
        if (orderTypeFilter !== 'offline' || posOrderType !== 'DINE-IN') {
            return null;
        }

        return (
            <input
                type="number"
                className={className}
                placeholder="Table no."
                value={tableNumber === '' || tableNumber == null ? '' : String(tableNumber)}
                onChange={(e) => setTableNumber(e.target.value)}
                min="1"
                aria-label="Table number"
            />
        );
    };

    const renderCartBadge = (className = 'pos-header-cart') => (
        <div
            className={className}
            title={`${totalQuantity} items in cart`}
            aria-label={`Cart: ${totalQuantity} items`}
        >
            <FaShoppingCart className="pos-header-cart-icon" />
            <span className="pos-header-cart-qty">{totalQuantity.toFixed(0)}</span>
        </div>
    );

    const renderTableAndCartGroup = (tableInputClassName = 'pos-table-input') => (
        <div className="pos-table-cart-group">
            {renderTableNumberInput(tableInputClassName)}
            {renderCartBadge()}
        </div>
    );

    const renderUnpaidBillsSection = (variant = 'sidebar') => {
        if (!canAccessUnpaidBills() || orderTypeFilter !== 'offline') return null;

        const isMaximize = variant === 'maximize';
        const expanded = isMaximize ? true : unpaidBillsExpanded;

        return (
            <div className={`pos-unpaid-bills${isMaximize ? ' pos-unpaid-bills--maximize' : ''}`}>
                {isMaximize ? null : (
                    <button
                        type="button"
                        className="pos-unpaid-bills__toggle"
                        onClick={() => setUnpaidBillsExpanded((open) => !open)}
                        aria-expanded={unpaidBillsExpanded}
                    >
                        <span>
                            Unpaid Bills
                            <span className="pos-unpaid-bills__count">
                                {unpaidBillsLoading ? '…' : unpaidBills.length}
                            </span>
                        </span>
                        <span className="pos-unpaid-bills__chevron" aria-hidden="true">
                            {unpaidBillsExpanded ? '▾' : '▸'}
                        </span>
                    </button>
                )}
                {expanded && (
                    <div className="pos-unpaid-bills__list">
                        {unpaidBillsLoading && unpaidBills.length === 0 ? (
                            <div className="pos-unpaid-bills__empty">Loading…</div>
                        ) : unpaidBills.length === 0 ? (
                            <div className="pos-unpaid-bills__empty">No unpaid bills today</div>
                        ) : (
                            unpaidBills.map((bill) => {
                                const id = bill.orderId ?? bill.orderNumber;
                                const displayId = formatDisplayOrderNo(bill, id);
                                const isActive =
                                    currentOrderId != null &&
                                    String(currentOrderId) === String(id);
                                const tableLabel = formatUnpaidBillTable(bill.tableNumber);
                                const itemCount = unpaidBillItemCount(bill);
                                const billTotal = parseFloat(bill.totalPrice || 0);
                                const timeLabel = formatUnpaidBillTime(bill);
                                return (
                                    <button
                                        type="button"
                                        key={String(id)}
                                        className={`pos-unpaid-bills__row${
                                            isActive ? ' is-active' : ''
                                        }`}
                                        onClick={() => loadUnpaidBill(bill)}
                                    >
                                        <span className="pos-unpaid-bills__row-main">
                                            <span className="pos-unpaid-bills__id">#{displayId}</span>
                                            {tableLabel ? (
                                                <span className="pos-unpaid-bills__table">
                                                    {tableLabel}
                                                </span>
                                            ) : null}
                                        </span>
                                        <span className="pos-unpaid-bills__row-meta">
                                            {itemCount > 0 ? `${itemCount} pcs` : ''}
                                            {itemCount > 0 && billTotal > 0 ? ' · ' : ''}
                                            {billTotal > 0 ? `₹${billTotal.toFixed(0)}` : ''}
                                            {(itemCount > 0 || billTotal > 0) && timeLabel
                                                ? ' · '
                                                : ''}
                                            {timeLabel}
                                        </span>
                                    </button>
                                );
                            })
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderFoodSearchInput = (className = 'pos-header-food-search') => (
        <div className={className}>
            <FaSearch className="search-icon-menu" />
            <input
                type="text"
                className="pos-header-search-input"
                placeholder="Search food items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search food items"
            />
            {searchQuery.trim() && (
                <button
                    type="button"
                    className="pos-header-search-clear"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                >
                    <FaTimes />
                </button>
            )}
        </div>
    );

    const renderFoodSearchResults = () => {
        if (!searchQuery.trim()) {
            return null;
        }

        return (
            <div className="maximize-food-search-results" role="listbox" aria-label="Search results">
                {filteredItems.length > 0 ? (
                    <>
                        <div className="maximize-food-search-results-header">
                            <span className="maximize-food-search-results-count">
                                {filteredItems.length} result{filteredItems.length !== 1 ? 's' : ''}
                            </span>
                        </div>
                        <ul className="maximize-food-search-list">
                            {filteredItems.map((item) => {
                                const imgUrl = item.imageUrl
                                    ? String(item.imageUrl).replace(/^%22+|%22+$/g, '').replace(/^"+|"+$/g, '').trim()
                                    : '';
                                const itemKey = getCartItemMatchKey(item);
                                const cartEntry = cart.find((c) => cartItemsMatch(c, itemKey));
                                    const cartQty = cartEntry
                                        ? (typeof cartEntry.quantity === 'number'
                                            ? cartEntry.quantity
                                            : parseInt(cartEntry.quantity, 10) || 0)
                                        : 0;
                                const stockLimit = parseDailyMenuStockLimit(item);
                                const atStockLimit = isAtDailyMenuStockLimit(item, cartQty);
                                const outOfStock =
                                    isOfflineDailyMenuOrder(orderTypeFilter) && stockLimit === 0;

                                return (
                                    <li key={itemKey} className="maximize-food-search-item" role="option">
                                        <div className="maximize-food-search-item-thumb">
                                            <img
                                                src={imgUrl || defaultFoodImage}
                                                alt=""
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = defaultFoodImage;
                                                }}
                                            />
                                        </div>
                                        <div className="maximize-food-search-item-body">
                                            <div className="maximize-food-search-item-name-row">
                                                <span className="maximize-food-search-item-name">
                                                    {formatFoodItemDisplayName(item.foodItemName || item.name) || 'N/A'}
                                                </span>
                                                {formatItemQty(item.quantity) && (
                                                    <span className="maximize-food-search-item-qty">
                                                        {formatItemQty(item.quantity)}
                                                    </span>
                                                )}
                                            </div>
                                            {orderTypeFilter !== 'catering' && (
                                                <span className="maximize-food-search-item-price">
                                                    ₹{parseFloat(item.price || 0).toFixed(2)}
                                                </span>
                                            )}
                                        </div>
                                        {cartQty > 0 ? (
                                            <span className="maximize-food-search-in-cart">{cartQty} in cart</span>
                                        ) : null}
                                        <button
                                            type="button"
                                            className="add-button maximize-food-search-add"
                                            onClick={() => {
                                                addToCart(item);
                                                setSearchQuery('');
                                            }}
                                            disabled={outOfStock || atStockLimit}
                                        >
                                            {outOfStock ? 'Out of stock' : 'Add'}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </>
                ) : (
                    <div className="maximize-food-search-empty">
                        No items found matching &quot;{searchQuery}&quot;
                    </div>
                )}
            </div>
        );
    };

    const renderMaximizeFoodSearch = () => (
        <div className="maximize-food-search-wrap">
            {renderFoodSearchInput('pos-header-food-search maximize-modal-food-search')}
            {renderFoodSearchResults()}
        </div>
    );

    return (
        <div className={`place-order-page${embedded ? ' place-order-embedded' : ''}`}>
            {!embedded && <Header />}

            <div className={`place-order-container mobile-pos-view-${mobilePosView}`}>
                {/* Food Item Listing - Grid layout (category tabs + search + cards); left sidebar removed */}
                <div className={`main-content main-content-full ${isCartOpen ? 'cart-open' : ''}`}>
                    {/* Live active-order counts — not for Self Checkout (page.pos only) */}
                    {orderTypeFilter === 'offline' && canAccessOrdersTab() && (
                        <DineInActiveOrders restaurantId={restaurantId} />
                    )}

                    <div className="content-header content-header-pos">
                        {(!embedded ||
                            (embedded &&
                                orderTypeFilter === 'catering' &&
                                location.state?.editOrderId != null)) && (
                            <button
                                type="button"
                                className="back-button"
                                aria-label="Back to orders"
                                onClick={() => {
                                    if (
                                        embedded &&
                                        orderTypeFilter === 'catering' &&
                                        location.state?.editOrderId != null
                                    ) {
                                        navigate('/home?tab=orders&orderType=catering', {
                                            replace: true,
                                            state: {},
                                        });
                                        return;
                                    }
                                    navigate('/home', { state: { activeTab: 'orders' } });
                                }}
                            >
                                <FaArrowLeft className="back-icon-menu" />
                            </button>
                        )}
                        {/* Search food items + category selector */}
                        <div className="content-header-actions pos-search-category-row pos-catalog-toolbar">
                            <div className="pos-catalog-search search-container search-container-pos">
                                <FaSearch className="search-icon-menu pos-catalog-search-icon" aria-hidden />
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Search menu by name…"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    aria-label="Search menu items"
                                />
                                {searchQuery.trim() && (
                                    <button
                                        type="button"
                                        className="pos-inline-search-clear"
                                        onClick={() => setSearchQuery('')}
                                        aria-label="Clear search"
                                    >
                                        <FaTimes />
                                    </button>
                                )}
                            </div>
                            <div className="pos-category-select-wrap pos-catalog-category">
                                <select
                                    id="pos-category-select"
                                    className="pos-category-select"
                                    aria-label="Category"
                                    value={selectedCategory}
                                    onChange={(e) => { setSelectedCategory(e.target.value); setSearchQuery(''); }}
                                >
                                    <option value="">All Categories</option>
                                    {categoryOptions.map((category) => (
                                        <option key={category} value={category}>
                                            {category}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Food items grid - cards with image, name, price, Add */}
                    <div className="food-items-grid-container">
                        {filteredItems.length > 0 ? (
                            <div className="food-items-grid">
                                {filteredItems.map((item) => {
                                    const imgUrl = item.imageUrl
                                        ? String(item.imageUrl).replace(/^%22+|%22+$/g, '').replace(/^"+|"+$/g, '').trim()
                                        : '';
                                    const itemKey = getCartItemMatchKey(item);
                                    const cartEntry = cart.find((c) => cartItemsMatch(c, itemKey));
                                    const cartQty = cartEntry
                                        ? (typeof cartEntry.quantity === 'number'
                                            ? cartEntry.quantity
                                            : parseInt(cartEntry.quantity, 10) || 0)
                                        : 0;
                                    const stockLimit = parseDailyMenuStockLimit(item);
                                    const atStockLimit = isAtDailyMenuStockLimit(item, cartQty);
                                    const outOfStock =
                                        isOfflineDailyMenuOrder(orderTypeFilter) && stockLimit === 0;
                                    return (
                                        <div
                                            key={itemKey}
                                            className={`food-item-card${cartQty > 0 ? ' is-selected' : ''}`}
                                        >
                                            <div className="food-item-card-image-wrap">
                                                <img
                                                    src={imgUrl || defaultFoodImage}
                                                    alt=""
                                                    className="food-item-card-image"
                                                    onError={(e) => { e.target.onerror = null; e.target.src = defaultFoodImage; }}
                                                />
                                                {cartQty > 0 && (
                                                    <span className="food-item-card-selected-badge">{cartQty}</span>
                                                )}
                                            </div>
                                            <div className="food-item-card-body">
                                                <div className="food-item-card-name-row">
                                                    <div className="food-item-card-title-block">
                                                        <span className="food-item-card-name" title={formatFoodItemDisplayName(item.foodItemName || item.name) || 'N/A'}>
                                                            {formatFoodItemDisplayName(item.foodItemName || item.name) || 'N/A'}
                                                        </span>
                                                        {formatItemQty(item.quantity) && (
                                                            <span className="food-item-card-qty">{formatItemQty(item.quantity)}</span>
                                                        )}
                                                    </div>
                                                    {orderTypeFilter !== 'catering' && (
                                                        <div className="food-item-card-price">
                                                            ₹{parseFloat(item.price || 0).toFixed(2)}
                                                        </div>
                                                    )}
                                                </div>
                                                {cartQty > 0 ? (
                                                    <div className="food-item-card-stepper">
                                                        <button
                                                            type="button"
                                                            className="food-item-card-stepper-btn"
                                                            onClick={() => updateQuantity(itemKey, cartQty - 1)}
                                                            aria-label="Decrease quantity"
                                                        >
                                                            &minus;
                                                        </button>
                                                        <span className="food-item-card-stepper-count">{cartQty}</span>
                                                        <button
                                                            type="button"
                                                            className="food-item-card-stepper-btn"
                                                            onClick={() => addToCart(item)}
                                                            disabled={atStockLimit}
                                                            aria-label="Increase quantity"
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="add-button add-button-card"
                                                        onClick={() => addToCart(item)}
                                                        disabled={outOfStock || atStockLimit}
                                                    >
                                                        {outOfStock ? 'Out of stock' : 'Add'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="food-items-grid-empty">
                                {searchQuery.trim()
                                    ? `No items found matching "${searchQuery}"`
                                    : orderTypeFilter === 'offline'
                                        ? "No items in today's daily menu. Add items under Daily Menu first."
                                        : 'No items found'}
                            </div>
                        )}
                    </div>
                    <div className="mobile-pos-next-bar">
                        <button
                            type="button"
                            className="mobile-pos-next-button"
                            onClick={() => setMobilePosView('order')}
                        >
                            <span>Next</span>
                            <span className="mobile-pos-next-count">
                                {cart.reduce(
                                    (total, item) =>
                                        total + (parseInt(item.quantity, 10) || 0),
                                    0
                                )}{' '}
                                items
                            </span>
                        </button>
                    </div>
                </div>

                {/* Right Order Details Sidebar - Always visible by default */}
                {isCartOpen && (
                    <div className="order-sidebar">
                        <div className="order-sidebar-header">
                            <div className="sidebar-header-top pos-sidebar-header-row">
                                <button
                                    type="button"
                                    className="mobile-pos-back-button"
                                    onClick={() => setMobilePosView('items')}
                                    aria-label="Back to food items"
                                >
                                    <FaArrowLeft aria-hidden="true" />
                                    <span>Items</span>
                                </button>
                                {orderTypeFilter === 'offline' ? (
                                    renderPosOrderTypeSelect()
                                ) : (
                                    <h3 className="pos-sidebar-title">
                                        {orderTypeFilter === 'catering' ? 'Catering' : 'Order Details'}
                                    </h3>
                                )}
                                {!showMaximizeModal && renderTableAndCartGroup()}
                                <button
                                    className="control-button maximize-button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowMaximizeModal(true);
                                    }}
                                    title="Open in window"
                                    type="button"
                                >
                                    <FaExternalLinkAlt style={{ fontSize: '14px' }} />
                                </button>
                            </div>
                        </div>

                        {/* Order ID Display */}
                        {currentOrderId && (
                            <div className="order-sidebar-order-id">
                                <span className="order-sidebar-order-id-label">Order #:</span>
                                <span className="order-sidebar-order-id-value">
                                    {currentDisplayOrderNo || currentOrderId}
                                </span>
                            </div>
                        )}

                        <div className="order-sidebar-body">
                                {renderUnpaidBillsSection()}
                                {/* Ordered Items Table */}
                                <div className="ordered-items-section">
                                    <table className="ordered-items-table">
                                        <thead>
                                            <tr>
                                                <th>Item</th>
                                                {orderTypeFilter !== 'catering' && (
                                                    <th className="ordered-items-price-col">Price</th>
                                                )}
                                                <th>Quantity</th>
                                                {orderTypeFilter !== 'catering' && <th>Total</th>}
                                                <th className="ordered-items-remove-col" aria-label="Remove" />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {cart.length > 0 ? (
                                                cart.map((item) => {
                                                    const itemId = item.foodItemId || item.id;
                                                    const qty = Number(item.quantity) || 0;
                                                    return (
                                                    <tr key={itemId}>
                                                        <td>{formatFoodItemDisplayName(item.foodItemName || item.name)}</td>
                                                        {orderTypeFilter !== 'catering' && (
                                                            <td className="ordered-items-price-col">
                                                                ₹{parseFloat(item.price || 0).toFixed(2)}
                                                            </td>
                                                        )}
                                                        <td className="ordered-items-qty-cell">
                                                            <div className="ordered-items-qty-input-wrap">
                                                                <input
                                                                    type="number"
                                                                    className="quantity-input"
                                                                    value={item.quantity || ''}
                                                                    min="1"
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        if (value === '') {
                                                                            setCart((prev) =>
                                                                                prev.map((cartItem) =>
                                                                                    cartItemsMatch(cartItem, item)
                                                                                        ? { ...cartItem, quantity: '' }
                                                                                        : cartItem
                                                                                )
                                                                            );
                                                                            return;
                                                                        }
                                                                        const newQty = parseInt(value);
                                                                        if (!isNaN(newQty) && newQty >= 1) {
                                                                            updateQuantity(itemId, newQty);
                                                                        }
                                                                    }}
                                                                    onBlur={(e) => {
                                                                        const value = e.target.value;
                                                                        if (value === '' || isNaN(parseInt(value)) || parseInt(value) < 1) {
                                                                            updateQuantity(itemId, 1);
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="ordered-items-qty-stepper food-item-card-stepper">
                                                                <button
                                                                    type="button"
                                                                    className="food-item-card-stepper-btn"
                                                                    onClick={() => updateQuantity(itemId, qty - 1)}
                                                                    aria-label="Decrease quantity"
                                                                >
                                                                    &minus;
                                                                </button>
                                                                <span className="food-item-card-stepper-count">{qty}</span>
                                                                <button
                                                                    type="button"
                                                                    className="food-item-card-stepper-btn"
                                                                    onClick={() => updateQuantity(itemId, qty + 1)}
                                                                    aria-label="Increase quantity"
                                                                >
                                                                    +
                                                                </button>
                                                            </div>
                                                        </td>
                                                        {orderTypeFilter !== 'catering' && (
                                                            <td>
                                                                <span>₹{(parseFloat(item.price || 0) * qty).toFixed(2)}</span>
                                                            </td>
                                                        )}
                                                        <td className="ordered-items-remove-col">
                                                            <button
                                                                className="remove-button"
                                                                onClick={() => removeFromCart(itemId)}
                                                            >
                                                                Remove
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan={orderTypeFilter === 'catering' ? 3 : 5} style={{ textAlign: 'center', padding: '20px' }}>
                                                        No items in cart
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Billing Summary */}
                                <div className="billing-summary">
                                    {orderTypeFilter === 'catering' ? (
                                        <div className="billing-row total-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
                                            <label style={{ fontWeight: '600', color: '#333' }}>Quoted Amount (₹)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                placeholder="Enter quoted amount"
                                                value={quotedAmount}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                                                        setQuotedAmount(value);
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '' || value == null) return;
                                                    const rounded = Math.round(parseFloat(value) * 100) / 100;
                                                    if (Number.isFinite(rounded) && rounded >= 0) {
                                                        setQuotedAmount(rounded.toFixed(2));
                                                    }
                                                }}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 12px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="billing-row">
                                                <span>Subtotal:</span>
                                                <span>₹{subtotal.toFixed(2)}</span>
                                            </div>
                                            <div className="billing-row">
                                                <span>GST ({gstPercentLabel}%):</span>
                                                <span>₹{gst.toFixed(2)}</span>
                                            </div>
                                            <div className="billing-row total-row">
                                                <span>Grand Total:</span>
                                                <span>₹{total.toFixed(2)}</span>
                                            </div>
                                        </>
                                    )}
                                    {orderTypeFilter === 'catering' && (
                                        <>
                                            <div className="billing-row" style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e0e0e0' }}>
                                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>Advance Payment:</label>
                                                <input
                                                    type="number"
                                                    placeholder="Enter advance payment (optional)"
                                                    value={advancePayment}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        // Allow empty value or valid number
                                                        if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                                                            setAdvancePayment(value);
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        const value = e.target.value;
                                                        if (value === '' || value === null) {
                                                            return;
                                                        }
                                                        // Round to 2 decimal places to avoid floating point issues
                                                        const totalRounded = Math.round(total * 100) / 100;
                                                        const advanceValue = Math.round(parseFloat(value) * 100) / 100;
                                                        // Ensure advance payment doesn't exceed total
                                                        if (advanceValue > totalRounded) {
                                                            setAdvancePayment(totalRounded.toFixed(2));
                                                        } else if (advanceValue > 0) {
                                                            // Round the entered value to 2 decimal places
                                                            setAdvancePayment(advanceValue.toFixed(2));
                                                        }
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px 12px',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '4px',
                                                        fontSize: '14px'
                                                    }}
                                                />
                                            </div>
                                            {advancePayment && parseFloat(advancePayment) > 0 && (
                                                <div className="billing-row" style={{ marginTop: '10px' }}>
                                                    <span style={{ fontWeight: '500', color: '#333' }}>Pending Amount:</span>
                                                    <span style={{ fontWeight: '600', color: '#d32f2f' }}>
                                                        ₹{(() => {
                                                            const totalRounded = Math.round(total * 100) / 100;
                                                            const advanceRounded = Math.round(parseFloat(advancePayment) * 100) / 100;
                                                            const pending = Math.max(0, Math.round((totalRounded - advanceRounded) * 100) / 100);
                                                            return pending.toFixed(2);
                                                        })()}
                                                    </span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Customer Details (catering keeps fields in scroll body) */}
                                {orderTypeFilter === 'catering' ? (
                                <div className="customer-details">
                                        <>
                                            <div className="form-group">
                                                <label>Customer Name</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter customer name"
                                                    value={customerName}
                                                    onChange={(e) => setCustomerName(e.target.value)}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Whatsapp Number</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter 10-digit Contact number"
                                                    value={contactNumber}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                                                        setContactNumber(value);
                                                    }}
                                                    maxLength="10"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Alt Number</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter 10-digit alternate number"
                                                    value={altNumber}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                                                        setAltNumber(value);
                                                    }}
                                                    maxLength="10"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Event Date</label>
                                                <input
                                                    type="date"
                                                    value={eventDate}
                                                    onChange={(e) => setEventDate(e.target.value)}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Event Location</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter event location"
                                                    value={eventLocation}
                                                    onChange={(e) => setEventLocation(e.target.value)}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Number of Guests</label>
                                                <input
                                                    type="number"
                                                    placeholder="Enter number of guests"
                                                    value={numberOfGuests}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/\D/g, '');
                                                        setNumberOfGuests(value);
                                                    }}
                                                    min="1"
                                                />
                                            </div>
                                            <div className="form-group payment-type-group">
                                                <label>Payment Type (Multiple Selection)</label>
                                                <div className="pos-payment-type-dropdown-wrap">
                                                    <div
                                                        role="button"
                                                        tabIndex={0}
                                                        className={`pos-payment-type-trigger pos-payment-type-trigger--compact${
                                                            showPaymentDropdown ? ' pos-payment-type-trigger--open' : ''
                                                        }`}
                                                        onClick={() => setShowPaymentDropdown(!showPaymentDropdown)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                setShowPaymentDropdown(!showPaymentDropdown);
                                                            }
                                                        }}
                                                    >
                                                        <span
                                                            className={
                                                                paymentTypes.length === 0
                                                                    ? 'pos-payment-type-trigger-label--placeholder'
                                                                    : undefined
                                                            }
                                                        >
                                                            {paymentTypes.length === 0
                                                                ? 'Select payment types'
                                                                : paymentTypes
                                                                      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
                                                                      .join(', ')}
                                                        </span>
                                                    </div>
                                                    {showPaymentDropdown && (
                                                        <div className="pos-payment-type-dropdown pos-payment-type-dropdown--dropup">
                                                            {['cash', 'card', 'upi', 'gpay', 'paytm', 'phonepe'].map((method) => (
                                                                <label
                                                                    key={method}
                                                                    className="pos-payment-type-dropdown__option pos-payment-type-dropdown__option--checkbox"
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={paymentTypes.includes(method)}
                                                                        onChange={(e) => {
                                                                            if (e.target.checked) {
                                                                                setPaymentTypes([...paymentTypes, method]);
                                                                            } else {
                                                                                setPaymentTypes(paymentTypes.filter(p => p !== method));
                                                                            }
                                                                        }}
                                                                    />
                                                                    <span className="pos-payment-type-dropdown__option-label">
                                                                        {method.charAt(0).toUpperCase() + method.slice(1)}
                                                                    </span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                </div>
                                ) : null}

                                </div>

                        {/* Offline sidebar: payment dropdown + customer (collapsed) + Clear/Cancel/Save/Payment */}
                        {orderTypeFilter !== 'catering' ? (
                            <div className="pos-sidebar-checkout">
                                <div className="pos-sidebar-payment-row">
                                    <label htmlFor="pos-sidebar-payment-method">Payment method</label>
                                    <select
                                        id="pos-sidebar-payment-method"
                                        className="pos-sidebar-payment-select"
                                        value={
                                            ['gpay', 'paytm', 'phonepe'].includes(paymentType)
                                                ? 'wallet'
                                                : paymentType || ''
                                        }
                                        onChange={(e) => {
                                            const method = e.target.value;
                                            if (!method) {
                                                setPaymentType('');
                                                return;
                                            }
                                            selectPosPayMethod(method);
                                        }}
                                        disabled={posReadyToPrint || posReadyForNew}
                                    >
                                        <option value="">Select payment method</option>
                                        <option value="card">Card</option>
                                        <option value="wallet">Wallet</option>
                                        <option value="cash">Cash</option>
                                        <option value="upi">UPI</option>
                                    </select>
                                </div>

                                <div className="maximize-customer-drawer pos-sidebar-customer-drawer">
                                    <button
                                        type="button"
                                        className="maximize-customer-drawer__toggle"
                                        onClick={() =>
                                            setShowOfflineCustomerDetailsInSidebar((open) => !open)
                                        }
                                        aria-expanded={showOfflineCustomerDetailsInSidebar}
                                    >
                                        <span>Customer Details</span>
                                        <span
                                            className="maximize-customer-drawer__chevron"
                                            aria-hidden="true"
                                        >
                                            {showOfflineCustomerDetailsInSidebar ? '▾' : '▸'}
                                        </span>
                                    </button>
                                    {showOfflineCustomerDetailsInSidebar && (
                                        <div className="maximize-customer-drawer__body">
                                            <div className="maximize-customer-drawer__fields">
                                                <div className="form-group">
                                                    <input
                                                        type="text"
                                                        placeholder="Customer name"
                                                        value={customerNameOffline}
                                                        onChange={(e) =>
                                                            setCustomerNameOffline(e.target.value)
                                                        }
                                                        aria-label="Customer name"
                                                        disabled={posReadyToPrint || posReadyForNew}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <input
                                                        type="text"
                                                        placeholder="Mobile number"
                                                        value={mobileNumber}
                                                        onChange={(e) => {
                                                            const value = e.target.value
                                                                .replace(/\D/g, '')
                                                                .slice(0, 10);
                                                            setMobileNumber(value);
                                                        }}
                                                        maxLength="10"
                                                        aria-label="Mobile number"
                                                        disabled={posReadyToPrint || posReadyForNew}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="order-action-buttons order-action-buttons--pos4">
                                    <button
                                        type="button"
                                        className="save-order-button"
                                        onClick={() => {
                                            setCart([]);
                                            setPosReadyToPrint(false);
                                            setPosReadyForNew(false);
                                            setOrderConfirmationData(null);
                                        }}
                                        disabled={
                                            (cart.length === 0 && !posReadyToPrint && !posReadyForNew) ||
                                            posReadyToPrint ||
                                            posReadyForNew
                                        }
                                    >
                                        Clear
                                    </button>
                                    <button
                                        type="button"
                                        className="save-order-button"
                                        onClick={handleCancelPosOrder}
                                        disabled={
                                            !currentOrderId ||
                                            posReadyToPrint ||
                                            posReadyForNew
                                        }
                                        title={
                                            currentOrderId
                                                ? 'Cancel this saved order'
                                                : 'Save the order first to enable Cancel'
                                        }
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="save-order-button"
                                        onClick={async () => {
                                            await handleSaveOrder();
                                        }}
                                        disabled={
                                            cart.length === 0 ||
                                            posReadyToPrint ||
                                            posReadyForNew
                                        }
                                    >
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        className="place-order-button"
                                        onClick={() => {
                                            if (posReadyForNew) {
                                                clearPosBillForm();
                                                return;
                                            }
                                            if (posReadyToPrint && orderConfirmationData) {
                                                const snapshot = buildPosResumeAfterPrintSnapshot();
                                                savePosResumeAfterPrintSnapshot(snapshot);
                                                navigate('/receipt', {
                                                    state: {
                                                        orderData: orderConfirmationData,
                                                        returnTo: businessAdminTab('dine-in'),
                                                        returnState: {
                                                            posResumeAfterPrint: true,
                                                            posBillSnapshot: snapshot,
                                                        },
                                                    },
                                                });
                                                return;
                                            }
                                            runPosPayment();
                                        }}
                                        disabled={
                                            posReadyForNew
                                                ? false
                                                : posReadyToPrint
                                                  ? !orderConfirmationData
                                                  : cart.length === 0
                                        }
                                    >
                                        {posReadyForNew
                                            ? 'New'
                                            : posReadyToPrint
                                              ? 'Print'
                                              : 'Payment'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                        <div className="order-action-buttons">
                            <button
                                className="save-order-button"
                                onClick={handleSaveOrder}
                                disabled={cart.length === 0}
                            >
                                Save
                            </button>
                            <button
                                className="place-order-button"
                                onClick={handlePlaceOrder}
                                disabled={cart.length === 0}
                            >
                                Complete
                            </button>
                        </div>
                        )}
                    </div>
                )}
            </div>

            {/* Contact Modal */}
            {showContactModal && (
                <div className="contact-modal-overlay" onClick={() => setShowContactModal(false)}>
                    <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="contact-modal-header">
                            <h2>Contact Information</h2>
                            <button className="contact-modal-close" onClick={() => setShowContactModal(false)}>×</button>
                        </div>
                        <div className="contact-modal-content">
                            <div className="contact-info-item">
                                <strong>Restaurant:</strong>
                                <span>{getRestaurantContact().name}</span>
                            </div>
                            <div className="contact-info-item">
                                <strong>Phone:</strong>
                                <span>{formatMobileDisplayOrFallback(getRestaurantContact().phone)}</span>
                            </div>
                            <div className="contact-info-item">
                                <strong>Email:</strong>
                                <span>{getRestaurantContact().email}</span>
                            </div>
                            <div className="contact-info-item">
                                <strong>Address:</strong>
                                <span>{getRestaurantContact().address}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Maximize Order Details Modal */}
            {showMaximizeModal && (
                <div
                    className="maximize-modal-overlay"
                    onClick={() => {
                        setShowPosPaymentQr(false);
                        setShowPosCardPayment(false);
                        setMaximizeRightPane('unpaid');
                        setShowMaximizeModal(false);
                    }}
                >
                    <div className="maximize-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="maximize-modal-header-section">
                            <div className="maximize-modal-header">
                                {orderTypeFilter === 'offline' ? (
                                    renderPosOrderTypeSelect('pos-order-type-select pos-order-type-select-modal')
                                ) : (
                                    <h3>
                                        {orderTypeFilter === 'catering' ? 'Catering' : 'Order Details'}
                                    </h3>
                                )}
                                {renderMaximizeFoodSearch()}
                                <div className="maximize-modal-controls">
                                    {renderTableAndCartGroup('pos-table-input pos-table-input-modal')}
                                    {orderTypeFilter === 'offline' && canAccessUnpaidBills() && (
                                        <button
                                            type="button"
                                            className={
                                                'maximize-unpaid-chip' +
                                                (maximizeRightPane === 'unpaid' ? ' is-active' : '')
                                            }
                                            onClick={() => {
                                                if (maximizeRightPane === 'unpaid') {
                                                    openMaximizeQrPane();
                                                } else {
                                                    openMaximizeUnpaidPane();
                                                }
                                            }}
                                            title={
                                                maximizeRightPane === 'unpaid'
                                                    ? 'Switch to QR payment'
                                                    : 'Show unpaid bills'
                                            }
                                        >
                                            Unpaid Bills (
                                            {unpaidBillsLoading ? '…' : unpaidBills.length})
                                        </button>
                                    )}
                                    <button
                                        className="maximize-modal-close"
                                        onClick={() => {
                                            setShowPosPaymentQr(false);
                                            setShowPosCardPayment(false);
                                            setMaximizeRightPane('unpaid');
                                            setShowMaximizeModal(false);
                                        }}
                                        title="Close"
                                    >
                                        <FaTimes />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div
                            className={`maximize-modal-content${
                                showPaymentTypeDropdown || showPaymentDropdown
                                    ? ' maximize-modal-content--payment-open'
                                    : ''
                            }${
                                orderTypeFilter === 'offline'
                                    ? ' maximize-modal-content--offline-split'
                                    : ' maximize-modal-content--split'
                            }`}
                        >
                            {orderTypeFilter === 'offline' ? (
                            <>
                            <div className="maximize-left-stack">
<div className="maximize-order-summary">
                                <div className="maximize-order-summary-title-row">
                                    <h4>Order Summary</h4>
                                    {currentOrderId ? (
                                        <span className="maximize-order-id-inline">
                                            Order #: <strong>{currentDisplayOrderNo || currentOrderId}</strong>
                                        </span>
                                    ) : null}
                                </div>
                                <div className="maximize-ordered-items-section">
                                    <table className="maximize-ordered-items-table">
                                        <thead>
                                            <tr>
                                                <th>Item</th>
                                                {orderTypeFilter !== 'catering' && <th>Price</th>}
                                                <th>Quantity</th>
                                                {orderTypeFilter !== 'catering' && <th>Total</th>}
                                                {orderTypeFilter === 'catering' && <th></th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {cart.length > 0 ? (
                                                cart.map((item) => (
                                                    <tr key={item.foodItemId || item.id}>
                                                        <td>{formatFoodItemDisplayName(item.foodItemName || item.name)}</td>
                                                        {orderTypeFilter !== 'catering' && (
                                                            <td>₹{parseFloat(item.price || 0).toFixed(2)}</td>
                                                        )}
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="quantity-input"
                                                                value={item.quantity || ''}
                                                                min="1"
                                                                onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    // Allow empty value for backspace
                                                                    if (value === '') {
                                                                        // Temporarily allow empty, will be validated on blur
                                                                        setCart((prev) =>
                                                                            prev.map((cartItem) =>
                                                                                cartItemsMatch(cartItem, item)
                                                                                    ? { ...cartItem, quantity: '' }
                                                                                    : cartItem
                                                                            )
                                                                        );
                                                                        return;
                                                                    }
                                                                    const newQty = parseInt(value);
                                                                    if (!isNaN(newQty) && newQty >= 1) {
                                                                        updateQuantity(item.foodItemId || item.id, newQty);
                                                                    }
                                                                }}
                                                                onBlur={(e) => {
                                                                    // Ensure minimum value of 1 when field loses focus
                                                                    const value = e.target.value;
                                                                    if (value === '' || isNaN(parseInt(value)) || parseInt(value) < 1) {
                                                                        updateQuantity(item.foodItemId || item.id, 1);
                                                                    }
                                                                }}
                                                            />
                                                        </td>
                                                        {orderTypeFilter !== 'catering' ? (
                                                            <td>
                                                                <div className="total-with-action">
                                                                    <span>₹{(parseFloat(item.price || 0) * item.quantity).toFixed(2)}</span>
                                                                    <button
                                                                        className="remove-button"
                                                                        onClick={() => removeFromCart(item.foodItemId || item.id)}
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        ) : (
                                                            <td>
                                                                <button
                                                                    className="remove-button"
                                                                    onClick={() => removeFromCart(item.foodItemId || item.id)}
                                                                >
                                                                    Remove
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={orderTypeFilter === 'catering' ? 3 : 4} style={{ textAlign: 'center', padding: '20px' }}>
                                                        No items in cart
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Billing Breakdown */}
                                <div className="maximize-billing-summary">
                                    {orderTypeFilter === 'catering' ? (
                                        <div className="billing-row total-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
                                            <label style={{ fontWeight: '600', color: '#333' }}>Quoted Amount (₹)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                placeholder="Enter quoted amount"
                                                value={quotedAmount}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                                                        setQuotedAmount(value);
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '' || value == null) return;
                                                    const rounded = Math.round(parseFloat(value) * 100) / 100;
                                                    if (Number.isFinite(rounded) && rounded >= 0) {
                                                        setQuotedAmount(rounded.toFixed(2));
                                                    }
                                                }}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 12px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="billing-row">
                                                <span>Subtotal:</span>
                                                <span>₹{subtotal.toFixed(2)}</span>
                                            </div>
                                            <div className="billing-row">
                                                <span>GST ({gstPercentLabel}%):</span>
                                                <span>₹{gst.toFixed(2)}</span>
                                            </div>
                                            <div className="billing-row total-row">
                                                <span>Total:</span>
                                                <span>₹{total.toFixed(2)}</span>
                                            </div>
                                        </>
                                    )}
                                    {orderTypeFilter === 'catering' && (
                                        <>
                                            <div className="billing-row" style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e0e0e0' }}>
                                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>Advance Payment:</label>
                                                <input
                                                    type="number"
                                                    placeholder="Enter advance payment (optional)"
                                                    value={advancePayment}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        // Allow empty value or valid number
                                                        if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                                                            setAdvancePayment(value);
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        const value = e.target.value;
                                                        if (value === '' || value === null) {
                                                            return;
                                                        }
                                                        // Round to 2 decimal places to avoid floating point issues
                                                        const totalRounded = Math.round(total * 100) / 100;
                                                        const advanceValue = Math.round(parseFloat(value) * 100) / 100;
                                                        // Ensure advance payment doesn't exceed total
                                                        if (advanceValue > totalRounded) {
                                                            setAdvancePayment(totalRounded.toFixed(2));
                                                        } else if (advanceValue > 0) {
                                                            // Round the entered value to 2 decimal places
                                                            setAdvancePayment(advanceValue.toFixed(2));
                                                        }
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px 12px',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '4px',
                                                        fontSize: '14px'
                                                    }}
                                                />
                                                {advancePayment && parseFloat(advancePayment) > 0 && (
                                                    <select
                                                        className="pos-advance-payment-select"
                                                        value={advancePaymentMethod}
                                                        onChange={(e) => setAdvancePaymentMethod(e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '8px 12px',
                                                            border: '1px solid #ddd',
                                                            borderRadius: '4px',
                                                            fontSize: '14px',
                                                            marginTop: '8px'
                                                        }}
                                                    >
                                                        <option value="">Select payment method for advance</option>
                                                        <option value="cash">Cash</option>
                                                        <option value="card">Card</option>
                                                        <option value="upi">UPI</option>
                                                        <option value="gpay">GPay</option>
                                                        <option value="paytm">Paytm</option>
                                                        <option value="phonepe">PhonePe</option>
                                                    </select>
                                                )}
                                            </div>
                                            {advancePayment && parseFloat(advancePayment) > 0 && (
                                                <div className="billing-row" style={{ marginTop: '10px' }}>
                                                    <span style={{ fontWeight: '500', color: '#333' }}>Pending Amount:</span>
                                                    <span style={{ fontWeight: '600', color: '#d32f2f' }}>
                                                        ₹{(() => {
                                                            const totalRounded = Math.round(total * 100) / 100;
                                                            const advanceRounded = Math.round(parseFloat(advancePayment) * 100) / 100;
                                                            const pending = Math.max(0, Math.round((totalRounded - advanceRounded) * 100) / 100);
                                                            return pending.toFixed(2);
                                                        })()}
                                                    </span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="maximize-customer-drawer">
                                <button
                                    type="button"
                                    className="maximize-customer-drawer__toggle"
                                    onClick={() =>
                                        setMaximizeCustomerDetailsOpen((open) => !open)
                                    }
                                    aria-expanded={maximizeCustomerDetailsOpen}
                                >
                                    <span>Customer Details</span>
                                    <span className="maximize-customer-drawer__chevron" aria-hidden="true">
                                        {maximizeCustomerDetailsOpen ? '▾' : '▸'}
                                    </span>
                                </button>
                                {maximizeCustomerDetailsOpen && (
                                    <div className="maximize-customer-drawer__body">
                                        <div className="maximize-customer-drawer__fields">
                                            <div className="form-group">
                                                <input
                                                    type="text"
                                                    placeholder="Enter customer name"
                                                    value={customerNameOffline}
                                                    onChange={(e) =>
                                                        setCustomerNameOffline(e.target.value)
                                                    }
                                                    aria-label="Customer name"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <input
                                                    type="text"
                                                    placeholder="10-digit mobile (optional)"
                                                    value={mobileNumber}
                                                    onChange={(e) => {
                                                        const value = e.target.value
                                                            .replace(/\D/g, '')
                                                            .slice(0, 10);
                                                        setMobileNumber(value);
                                                    }}
                                                    maxLength="10"
                                                    aria-label="Mobile number"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="maximize-left-actions">
                                <div className="pos-action-pad pos-action-pad--compact">
                                    <div className="pos-action-pad__row pos-action-pad__row--pay4">
                                        {['card', 'wallet', 'cash', 'upi'].map((method) => {
                                            const isSelected =
                                                paymentType === method ||
                                                (method === 'wallet' &&
                                                    ['gpay', 'paytm', 'phonepe'].includes(
                                                        paymentType
                                                    ));
                                            return (
                                                <button
                                                    type="button"
                                                    key={method}
                                                    className={
                                                        'pos-action-btn pos-action-btn--pay' +
                                                        (isSelected ? ' is-selected' : '')
                                                    }
                                                    aria-pressed={isSelected}
                                                    onClick={() => selectPosPayMethod(method)}
                                                    disabled={posReadyToPrint || posReadyForNew}
                                                >
                                                    {method}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <div className="pos-action-pad__row pos-action-pad__row--4">
                                        <button
                                            type="button"
                                            className="pos-action-btn pos-action-btn--primary"
                                            onClick={() => {
                                                setCart([]);
                                                setPosReadyToPrint(false);
                                                setPosReadyForNew(false);
                                                setOrderConfirmationData(null);
                                            }}
                                            disabled={
                                                (cart.length === 0 && !posReadyToPrint && !posReadyForNew) ||
                                                posReadyToPrint ||
                                                posReadyForNew
                                            }
                                        >
                                            Clear
                                        </button>
                                        <button
                                            type="button"
                                            className="pos-action-btn pos-action-btn--primary"
                                            onClick={handleCancelPosOrder}
                                            disabled={
                                                !currentOrderId ||
                                                posReadyToPrint ||
                                                posReadyForNew
                                            }
                                            title={
                                                currentOrderId
                                                    ? 'Cancel this saved order'
                                                    : 'Save the order first to enable Cancel'
                                            }
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="pos-action-btn pos-action-btn--primary"
                                            onClick={async () => {
                                                await handleSaveOrder();
                                            }}
                                            disabled={
                                                cart.length === 0 ||
                                                posReadyToPrint ||
                                                posReadyForNew
                                            }
                                        >
                                            Save
                                        </button>
                                        <button
                                            type="button"
                                            className="pos-action-btn pos-action-btn--primary"
                                            onClick={() => {
                                                if (posReadyForNew) {
                                                    clearPosBillForm();
                                                    return;
                                                }
                                                if (posReadyToPrint && orderConfirmationData) {
                                                    const snapshot = buildPosResumeAfterPrintSnapshot();
                                                    savePosResumeAfterPrintSnapshot(snapshot);
                                                    navigate('/receipt', {
                                                        state: {
                                                            orderData: orderConfirmationData,
                                                            returnTo: businessAdminTab('dine-in'),
                                                            returnState: {
                                                                posResumeAfterPrint: true,
                                                                posBillSnapshot: snapshot,
                                                            },
                                                        },
                                                    });
                                                    return;
                                                }
                                                runPosPayment();
                                            }}
                                            disabled={
                                                posReadyForNew
                                                    ? false
                                                    : posReadyToPrint
                                                      ? !orderConfirmationData
                                                      : cart.length === 0
                                            }
                                        >
                                            {posReadyForNew
                                                ? 'New'
                                                : posReadyToPrint
                                                  ? 'Print'
                                                  : 'Payment'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            </div>

                            <div className="maximize-unpaid-pane">
                                {maximizeRightPane === 'qr' ? (
                                    <div className="pos-payment-qr-panel pos-payment-qr-panel--pane">
                                        <div className="pos-payment-meta-row">
                                            <span>
                                                PT:{' '}
                                                <strong>
                                                    {['gpay', 'paytm', 'phonepe'].includes(paymentType)
                                                        ? 'WALLET'
                                                        : 'UPI'}
                                                </strong>
                                            </span>
                                            <span>
                                                Amnt:{' '}
                                                <strong>
                                                    ₹{Number(posPayAmount || 0).toFixed(2)}
                                                </strong>
                                            </span>
                                        </div>
                                        {posUpiQrImageUrl ? (
                                            <div className="pos-payment-qr-panel__qr">
                                                <img
                                                    src={posUpiQrImageUrl}
                                                    alt="UPI payment QR code"
                                                    width={200}
                                                    height={200}
                                                />
                                                <p className="pos-payment-qr-panel__vpa-display">
                                                    {posUpiVpa || posUpiVpaDraft}
                                                </p>
                                            </div>
                                        ) : (
                                            <p className="pos-payment-qr-panel__missing">
                                                Restaurant UPI ID is not configured.
                                            </p>
                                        )}
                                        <div className="pos-payment-qr-panel__send">
                                            {!posUpiSendExpanded ? (
                                                <button
                                                    type="button"
                                                    className="pos-action-btn pos-action-btn--primary pos-upi-send-expand"
                                                    onClick={() => setPosUpiSendExpanded(true)}
                                                    disabled={!posUpiQrImageUrl}
                                                >
                                                    Send link to mobile
                                                </button>
                                            ) : (
                                                <>
                                                    <div className="pos-payment-qr-panel__send-head">
                                                        <span>Send link to mobile</span>
                                                        <button
                                                            type="button"
                                                            className="pos-upi-send-collapse"
                                                            onClick={() => setPosUpiSendExpanded(false)}
                                                            title="Close"
                                                            aria-label="Close send link"
                                                        >
                                                            <FaTimes />
                                                        </button>
                                                    </div>
                                                    <div className="pos-payment-qr-panel__vpa-row">
                                                        <input
                                                            id="pos-upi-send-phone"
                                                            type="tel"
                                                            inputMode="numeric"
                                                            maxLength={10}
                                                            placeholder="Customer mobile"
                                                            value={posUpiSendPhone}
                                                            onChange={(e) =>
                                                                setPosUpiSendPhone(
                                                                    e.target.value
                                                                        .replace(/\D/g, '')
                                                                        .slice(0, 10)
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div className="pos-payment-qr-panel__send-actions">
                                                        <button
                                                            type="button"
                                                            className="pos-action-btn pos-action-btn--primary pos-upi-send-action"
                                                            onClick={sendUpiLinkSms}
                                                            disabled={!posUpiQrImageUrl}
                                                        >
                                                            SMS
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="pos-action-btn pos-action-btn--primary pos-upi-send-action"
                                                            onClick={sendUpiLinkWhatsApp}
                                                            disabled={!posUpiQrImageUrl}
                                                        >
                                                            WhatsApp
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div className="pos-payment-qr-panel__actions">
                                            <button
                                                type="button"
                                                className="pos-action-btn pos-action-btn--ghost"
                                                onClick={openMaximizeUnpaidPane}
                                            >
                                                Back
                                            </button>
                                            <button
                                                type="button"
                                                className="pos-action-btn pos-action-btn--primary"
                                                onClick={confirmPosQrPayment}
                                                disabled={cart.length === 0 || !posUpiQrImageUrl}
                                            >
                                                Payment received
                                            </button>
                                        </div>
                                    </div>
                                ) : maximizeRightPane === 'card' ? (
                                    <div className="pos-payment-qr-panel pos-payment-qr-panel--pane">
                                        <div className="pos-payment-meta-row">
                                            <span>
                                                PT: <strong>CARD</strong>
                                            </span>
                                            <span>
                                                Amnt:{' '}
                                                <strong>
                                                    ₹{Number(posPayAmount || 0).toFixed(2)}
                                                </strong>
                                            </span>
                                        </div>
                                        <div className="pos-payment-card-fields">
                                            <label htmlFor="pos-card-last4">
                                                Card last 4 digits
                                            </label>
                                            <input
                                                id="pos-card-last4"
                                                type="tel"
                                                inputMode="numeric"
                                                maxLength={4}
                                                placeholder="Optional"
                                                value={posCardLast4}
                                                onChange={(e) =>
                                                    setPosCardLast4(
                                                        e.target.value.replace(/\D/g, '').slice(0, 4)
                                                    )
                                                }
                                            />
                                            <label htmlFor="pos-card-rrn">
                                                Bank RRN / approval code
                                            </label>
                                            <input
                                                id="pos-card-rrn"
                                                type="text"
                                                placeholder="Optional"
                                                value={posCardBankRrn}
                                                onChange={(e) => setPosCardBankRrn(e.target.value)}
                                            />
                                            <label htmlFor="pos-card-txn">Transaction ID</label>
                                            <input
                                                id="pos-card-txn"
                                                type="text"
                                                placeholder="Optional"
                                                value={posCardTxnId}
                                                onChange={(e) => setPosCardTxnId(e.target.value)}
                                            />
                                        </div>
                                        <div className="pos-payment-qr-panel__actions">
                                            <button
                                                type="button"
                                                className="pos-action-btn pos-action-btn--ghost"
                                                onClick={openMaximizeUnpaidPane}
                                            >
                                                Back
                                            </button>
                                            <button
                                                type="button"
                                                className="pos-action-btn pos-action-btn--primary"
                                                onClick={confirmPosCardPayment}
                                                disabled={cart.length === 0}
                                            >
                                                Pay
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    renderUnpaidBillsSection('maximize')
                                )}
                            </div>
                            </>
                            ) : (
                            <>
<div className="maximize-order-summary">
                                <div className="maximize-order-summary-title-row">
                                    <h4>Order Summary</h4>
                                    {currentOrderId ? (
                                        <span className="maximize-order-id-inline">
                                            Order #: <strong>{currentDisplayOrderNo || currentOrderId}</strong>
                                        </span>
                                    ) : null}
                                </div>
                                <div className="maximize-ordered-items-section">
                                    <table className="maximize-ordered-items-table">
                                        <thead>
                                            <tr>
                                                <th>Item</th>
                                                {orderTypeFilter !== 'catering' && <th>Price</th>}
                                                <th>Quantity</th>
                                                {orderTypeFilter !== 'catering' && <th>Total</th>}
                                                {orderTypeFilter === 'catering' && <th></th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {cart.length > 0 ? (
                                                cart.map((item) => (
                                                    <tr key={item.foodItemId || item.id}>
                                                        <td>{formatFoodItemDisplayName(item.foodItemName || item.name)}</td>
                                                        {orderTypeFilter !== 'catering' && (
                                                            <td>₹{parseFloat(item.price || 0).toFixed(2)}</td>
                                                        )}
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="quantity-input"
                                                                value={item.quantity || ''}
                                                                min="1"
                                                                onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    // Allow empty value for backspace
                                                                    if (value === '') {
                                                                        // Temporarily allow empty, will be validated on blur
                                                                        setCart((prev) =>
                                                                            prev.map((cartItem) =>
                                                                                cartItemsMatch(cartItem, item)
                                                                                    ? { ...cartItem, quantity: '' }
                                                                                    : cartItem
                                                                            )
                                                                        );
                                                                        return;
                                                                    }
                                                                    const newQty = parseInt(value);
                                                                    if (!isNaN(newQty) && newQty >= 1) {
                                                                        updateQuantity(item.foodItemId || item.id, newQty);
                                                                    }
                                                                }}
                                                                onBlur={(e) => {
                                                                    // Ensure minimum value of 1 when field loses focus
                                                                    const value = e.target.value;
                                                                    if (value === '' || isNaN(parseInt(value)) || parseInt(value) < 1) {
                                                                        updateQuantity(item.foodItemId || item.id, 1);
                                                                    }
                                                                }}
                                                            />
                                                        </td>
                                                        {orderTypeFilter !== 'catering' ? (
                                                            <td>
                                                                <div className="total-with-action">
                                                                    <span>₹{(parseFloat(item.price || 0) * item.quantity).toFixed(2)}</span>
                                                                    <button
                                                                        className="remove-button"
                                                                        onClick={() => removeFromCart(item.foodItemId || item.id)}
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        ) : (
                                                            <td>
                                                                <button
                                                                    className="remove-button"
                                                                    onClick={() => removeFromCart(item.foodItemId || item.id)}
                                                                >
                                                                    Remove
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={orderTypeFilter === 'catering' ? 3 : 4} style={{ textAlign: 'center', padding: '20px' }}>
                                                        No items in cart
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Billing Breakdown */}
                                <div className="maximize-billing-summary">
                                    {orderTypeFilter === 'catering' ? (
                                        <div className="billing-row total-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
                                            <label style={{ fontWeight: '600', color: '#333' }}>Quoted Amount (₹)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                placeholder="Enter quoted amount"
                                                value={quotedAmount}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                                                        setQuotedAmount(value);
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '' || value == null) return;
                                                    const rounded = Math.round(parseFloat(value) * 100) / 100;
                                                    if (Number.isFinite(rounded) && rounded >= 0) {
                                                        setQuotedAmount(rounded.toFixed(2));
                                                    }
                                                }}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 12px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="billing-row">
                                                <span>Subtotal:</span>
                                                <span>₹{subtotal.toFixed(2)}</span>
                                            </div>
                                            <div className="billing-row">
                                                <span>GST ({gstPercentLabel}%):</span>
                                                <span>₹{gst.toFixed(2)}</span>
                                            </div>
                                            <div className="billing-row total-row">
                                                <span>Total:</span>
                                                <span>₹{total.toFixed(2)}</span>
                                            </div>
                                        </>
                                    )}
                                    {orderTypeFilter === 'catering' && (
                                        <>
                                            <div className="billing-row" style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e0e0e0' }}>
                                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>Advance Payment:</label>
                                                <input
                                                    type="number"
                                                    placeholder="Enter advance payment (optional)"
                                                    value={advancePayment}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        // Allow empty value or valid number
                                                        if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                                                            setAdvancePayment(value);
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        const value = e.target.value;
                                                        if (value === '' || value === null) {
                                                            return;
                                                        }
                                                        // Round to 2 decimal places to avoid floating point issues
                                                        const totalRounded = Math.round(total * 100) / 100;
                                                        const advanceValue = Math.round(parseFloat(value) * 100) / 100;
                                                        // Ensure advance payment doesn't exceed total
                                                        if (advanceValue > totalRounded) {
                                                            setAdvancePayment(totalRounded.toFixed(2));
                                                        } else if (advanceValue > 0) {
                                                            // Round the entered value to 2 decimal places
                                                            setAdvancePayment(advanceValue.toFixed(2));
                                                        }
                                                    }}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px 12px',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '4px',
                                                        fontSize: '14px'
                                                    }}
                                                />
                                                {advancePayment && parseFloat(advancePayment) > 0 && (
                                                    <select
                                                        className="pos-advance-payment-select"
                                                        value={advancePaymentMethod}
                                                        onChange={(e) => setAdvancePaymentMethod(e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '8px 12px',
                                                            border: '1px solid #ddd',
                                                            borderRadius: '4px',
                                                            fontSize: '14px',
                                                            marginTop: '8px'
                                                        }}
                                                    >
                                                        <option value="">Select payment method for advance</option>
                                                        <option value="cash">Cash</option>
                                                        <option value="card">Card</option>
                                                        <option value="upi">UPI</option>
                                                        <option value="gpay">GPay</option>
                                                        <option value="paytm">Paytm</option>
                                                        <option value="phonepe">PhonePe</option>
                                                    </select>
                                                )}
                                            </div>
                                            {advancePayment && parseFloat(advancePayment) > 0 && (
                                                <div className="billing-row" style={{ marginTop: '10px' }}>
                                                    <span style={{ fontWeight: '500', color: '#333' }}>Pending Amount:</span>
                                                    <span style={{ fontWeight: '600', color: '#d32f2f' }}>
                                                        ₹{(() => {
                                                            const totalRounded = Math.round(total * 100) / 100;
                                                            const advanceRounded = Math.round(parseFloat(advancePayment) * 100) / 100;
                                                            const pending = Math.max(0, Math.round((totalRounded - advanceRounded) * 100) / 100);
                                                            return pending.toFixed(2);
                                                        })()}
                                                    </span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

<div
                                className={`maximize-customer-details${
                                    showPaymentTypeDropdown || showPaymentDropdown
                                        ? ' maximize-customer-details--payment-open'
                                        : ''
                                }`}
                            >
                                <h4>Customer Details</h4>
                                        <div className="form-group">
                                            <label>Customer Name</label>
                                            <input
                                                type="text"
                                                placeholder="Enter customer name"
                                                value={customerName}
                                                onChange={(e) => setCustomerName(e.target.value)}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Contact Number</label>
                                            <input
                                                type="text"
                                                placeholder="Enter 10-digit contact number"
                                                value={contactNumber}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                                                    setContactNumber(value);
                                                }}
                                                maxLength="10"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Alt Number</label>
                                            <input
                                                type="text"
                                                placeholder="Enter 10-digit alternate number"
                                                value={altNumber}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                                                    setAltNumber(value);
                                                }}
                                                maxLength="10"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Event Date</label>
                                            <input
                                                type="date"
                                                value={eventDate}
                                                onChange={(e) => setEventDate(e.target.value)}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Event Location</label>
                                            <input
                                                type="text"
                                                placeholder="Enter event location"
                                                value={eventLocation}
                                                onChange={(e) => setEventLocation(e.target.value)}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Number of Guests</label>
                                            <input
                                                type="number"
                                                placeholder="Enter number of guests"
                                                value={numberOfGuests}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/\D/g, '');
                                                    setNumberOfGuests(value);
                                                }}
                                                min="1"
                                            />
                                        </div>
                                        <div className="form-group payment-type-group">
                                            <label>Payment Type (Multiple Selection)</label>
                                            <div className="pos-payment-type-dropdown-wrap">
                                                <div
                                                    role="button"
                                                    tabIndex={0}
                                                    className={`pos-payment-type-trigger${
                                                        showPaymentDropdown ? ' pos-payment-type-trigger--open' : ''
                                                    }`}
                                                    onClick={() => setShowPaymentDropdown(!showPaymentDropdown)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            setShowPaymentDropdown(!showPaymentDropdown);
                                                        }
                                                    }}
                                                >
                                                    <span
                                                        className={
                                                            paymentTypes.length === 0
                                                                ? 'pos-payment-type-trigger-label--placeholder'
                                                                : undefined
                                                        }
                                                    >
                                                        {paymentTypes.length === 0
                                                            ? 'Select payment types'
                                                            : paymentTypes.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ')}
                                                    </span>
                                                </div>
                                                {showPaymentDropdown && (
                                                    <div className="pos-payment-type-dropdown pos-payment-type-dropdown--dropup">
                                                        {['cash', 'card', 'upi', 'gpay', 'paytm', 'phonepe'].map((method) => (
                                                            <label
                                                                key={method}
                                                                className="pos-payment-type-dropdown__option pos-payment-type-dropdown__option--checkbox"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={paymentTypes.includes(method)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setPaymentTypes([...paymentTypes, method]);
                                                                        } else {
                                                                            setPaymentTypes(paymentTypes.filter(p => p !== method));
                                                                        }
                                                                    }}
                                                                />
                                                                <span className="pos-payment-type-dropdown__option-label">
                                                                    {method.charAt(0).toUpperCase() + method.slice(1)}
                                                                </span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                <div className="maximize-order-total">
                                    <div className="billing-row total-row">
                                        <span>Grand Total:</span>
                                        <span>₹{total.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="maximize-modal-footer">
                                <button
                                    className="save-order-button-modal"
                                    onClick={() => {
                                        handleSaveOrder();
                                        setShowMaximizeModal(false);
                                    }}
                                    disabled={cart.length === 0}
                                >
                                    Save
                                </button>
                                <button
                                    className="place-order-button-modal"
                                    onClick={() => {
                                        handlePlaceOrder();
                                        setShowMaximizeModal(false);
                                    }}
                                    disabled={cart.length === 0}
                                >
                                    Complete
                                </button>
                            </div>
                            </>
                            )}
                        </div>

                    </div>
                </div>
            )}

            {/* Order Confirmation Popup */}
            {showOrderConfirmation && (
                <OrderConfirmation 
                    orderData={orderConfirmationData} 
                    onClose={() => setShowOrderConfirmation(false)}
                />
            )}
        </div>
    );
};

export default PlaceOrder;
